import "./style.css";
import {
  fetchCashCounts,
  fetchFeePayouts,
  fetchTickets,
  MutationConflictError,
  createFieldTicket,
  addFieldTicketSeat,
  saveCashCount,
  saveFeePayout,
  sendMutation,
  updateTicketOps,
} from "./api";
import {
  buildFeeSnapshot,
  computeBalance,
  computeBalanceDelta,
  computeDoorIncome,
  computeVariance,
  DENOMINATIONS,
  normalizeCashRecord,
  normalizeCounts,
  ONSITE_DOOR_PRICES,
  previousCheckpoint,
  type CashCheckpoint,
  type Denomination,
} from "./cash";
import {
  appendOutbox,
  loadLocalSnapshot,
  removeOutbox,
  replaceLocalSnapshot,
  saveCashLocal,
  saveDeferredCreditsLocal,
  saveFeeLocal,
  saveTicketsLocal,
  setFieldMeta,
  type DeviceRole,
  type FieldMutation,
} from "./db";
import { DEMO_TICKETS } from "./demo";
import {
  afternoonNamingHintLabel,
  allowsCheckIn,
  allowsPerformerAssignment,
  couponEligibleOnRedeem,
  deferredRedeemPackages,
  displayPhone,
  displayTicketName,
  isComplete,
  isFieldTableTicket,
  matchesDeferredCredit,
  matchesTicket,
  OPS_STATUS_LABELS,
  paymentNeedsAttention,
  SESSION_LABELS,
  ticketTypeFromOpsStatus,
} from "./domain";
import { FIELD_TIMEOUT_MESSAGE, fieldSetDeviceRole, runCheckpointSync } from "./fieldClient";
import {
  buildAfternoonFeeRows,
  buildEveningFeeRows,
  buildFeeSummary,
  NOMINATION_FEE_PER_CHECKIN,
} from "./fees";
import { createId } from "./id";
import {
  applyDeferredLocally,
  applyFieldMutationLocally,
  canSellField,
  canWriteCash,
  canWriteFees,
  fieldMutationRequiresOutbox,
  isLocalDraftTicketId,
  mergeTicketsAfterCheckpointPull,
} from "./ledger";
import { isCounterPaymentPending, onsiteTicketPlan, phoneDigits } from "./settle";
import { storage } from "./storage";
import { withPendingMutations } from "./sync";
import {
  EVENT_ID,
  type CheckInMutation,
  type DeferredCredit,
  type FeePayoutRecord,
  type PerformerMutation,
  type Session,
  type SyncConflict,
  type SyncMutation,
  type Ticket,
  type TicketOpsStatus,
  type TicketUnit,
} from "./types";

const ONSITE_PACKAGE_LABELS = {
  afternoon: "+現場午場",
  evening: "+現場晚場",
  both: "+現場通票",
} as const;

type Filter = "all" | "pending" | "complete" | "payment";
type SearchMode = "phone" | "name";
type AppView = "checkin" | "field" | "fees" | "cash";

function searchPlaceholder(mode: SearchMode): string {
  return mode === "phone" ? "電話末三碼（含延用）" : "登記名稱（中／英，含延用）";
}

function searchEmptyHint(mode: SearchMode): string {
  return mode === "phone"
    ? "試試電話末三碼（含 7/11 延用額度），或切到「名稱」用登記名稱搜尋"
    : "試試登記名稱（中／英，含 7/11 延用），或切回「電話」用末三碼";
}

function isFieldMode(endpoint: string): boolean {
  return storage.isFieldBackend(endpoint);
}

const CASH_LABELS: Record<CashCheckpoint, string> = {
  opening: "原始金額",
  afternoon: "午場結算",
  evening: "晚場結算",
};

function unitsForTicket(ticket: Ticket): TicketUnit[] {
  if (ticket.units?.length) return ticket.units;
  return ticket.sessions.flatMap((session) => {
    const sourcePerformer =
      session === "afternoon" ? ticket.afternoonPerformer : ticket.eveningPerformer;
    const performer = /^[(（]/.test(sourcePerformer || "") ? "尚未指名" : sourcePerformer || "尚未指名";
    const checkedCount = ticket.checkIns?.[session]?.count || 0;
    return Array.from({ length: ticket.quantity }, (_, index) => ({
      id: `${ticket.id}:${index + 1}:${session}`,
      seat: index + 1,
      session,
      performer,
      checkedIn: index < checkedCount,
    }));
  });
}

function normalizeTicket(ticket: Ticket): Ticket {
  const units = unitsForTicket(ticket);
  return {
    ...ticket,
    units,
    checkIns: {
      afternoon: {
        ...ticket.checkIns.afternoon,
        count: units.filter((unit) => unit.session === "afternoon" && unit.checkedIn).length,
      },
      evening: {
        ...ticket.checkIns.evening,
        count: units.filter((unit) => unit.session === "evening" && unit.checkedIn).length,
      },
    },
  };
}

function optionsFromTickets(tickets: Ticket[], session: Session): string[] {
  return [...new Set(tickets.flatMap((ticket) => unitsForTicket(ticket)).filter((unit) => unit.session === session).map((unit) => unit.performer).filter((performer) => performer !== "尚未指名"))];
}

const savedConfig = storage.getConfig();
const cachedTickets = storage.getTickets().map(normalizeTicket);
const cachedLastSync = storage.getLastSync();
const cachedPerformerOptions = storage.getPerformerOptions();
const defaultConfig = { endpoint: "/api/field", token: storage.getFieldToken() };
const initialConfig = savedConfig.endpoint ? savedConfig : defaultConfig;
const state = {
  tickets: cachedTickets,
  queue: storage.getQueue(),
  conflicts: storage.getConflicts(),
  performerOptions: cachedPerformerOptions,
  activeSession: "afternoon" as Session,
  config: initialConfig,
  query: "",
  searchMode: "phone" as SearchMode,
  filter: "all" as Filter,
  selectedId: "",
  initialLoading: false,
  syncing: false,
  syncError: null as string | null,
  hasCachedSnapshot: cachedTickets.length > 0 || Boolean(cachedLastSync),
  online: navigator.onLine,
  demo: !initialConfig.endpoint,
  lastSync: cachedLastSync,
  toast: "",
  forceCheckinPrompt: null as null | { ticketId: string; unitId: string },
  view: "checkin" as AppView,
  feePayouts: [] as FeePayoutRecord[],
  feeQueue: [] as Array<{ session: Session; performer: string; amountPaid: number }>,
  cashCounts: storage.getCashCounts(),
  cashQueue: [] as CashCheckpoint[],
  fieldBusy: false,
  /** local-* ticket id → extra seats added before server create finished */
  fieldPendingExtraSeats: {} as Record<string, number>,
  /** field ticket ids with create still awaiting successful checkpoint pull */
  fieldPendingCreateIds: {} as Record<string, true>,
  deviceRole: (storage.getDeviceRole() || "general") as DeviceRole,
  pendingOutbox: 0,
  autoNotes: [] as string[],
  deferredCredits: [] as DeferredCredit[],
};

if (!state.tickets.length && state.demo) {
  state.tickets = structuredClone(DEMO_TICKETS);
}

function fieldConfig() {
  return {
    endpoint: state.config.endpoint || "/api/field",
    token: state.config.token || storage.getFieldToken(),
  };
}

async function persistTickets() {
  storage.setTickets(state.tickets);
  if (isFieldMode(state.config.endpoint)) {
    await saveTicketsLocal(state.tickets);
  }
}

async function enqueueFieldMutation(mutation: FieldMutation) {
  // Invariant: every FieldMutation kind must reach outbox (see FIELD_MUTATION_REQUIRES_OUTBOX).
  void fieldMutationRequiresOutbox(mutation.kind);
  state.tickets = applyFieldMutationLocally(state.tickets, mutation).map(normalizeTicket);
  state.deferredCredits = applyDeferredLocally(state.deferredCredits, mutation);
  await appendOutbox(mutation);
  state.pendingOutbox += 1;
  await persistTickets();
  if (
    mutation.kind === "redeem_deferred" ||
    mutation.kind === "coupon_issue"
  ) {
    await saveDeferredCreditsLocal(state.deferredCredits);
  }
  if (mutation.kind === "fee_payout") {
    await saveFeeLocal({
      session: mutation.session,
      performer: mutation.performer,
      amountPaid: mutation.amountPaid,
      updatedAt: mutation.createdAt,
      device: mutation.deviceId,
    });
  }
  if (mutation.kind === "cash_count") {
    await saveCashLocal({
      checkpoint: mutation.checkpoint,
      counts: normalizeCounts(mutation.counts),
      balance: mutation.balance,
      income: mutation.income,
      feeSnapshot: mutation.feeSnapshot,
      feeLocked: mutation.feeLocked,
      updatedAt: mutation.createdAt,
      device: mutation.deviceId,
    });
  }
}

/**
 * Remap a leftover `local-*` draft to a stable id and enqueue create (+ redeem).
 * Primary create paths must not leave local-* durable; this is for orphans / legacy.
 */
async function materializeLocalFieldDraft(draft: Ticket): Promise<Ticket> {
  if (!isLocalDraftTicketId(draft.id)) return draft;
  const stableId = `${storage.getDeviceId()}-${createId().slice(0, 8)}`;
  const remapped = remapTicketId(draft, stableId);
  state.tickets = state.tickets.map((item) => (item.id === draft.id ? remapped : item));
  delete state.fieldPendingExtraSeats[draft.id];
  state.fieldPendingCreateIds[stableId] = true;
  delete state.fieldPendingCreateIds[draft.id];
  if (draft.creditSourceId) {
    state.deferredCredits = state.deferredCredits.map((credit) =>
      credit.id === draft.creditSourceId
        ? { ...credit, redeemed: true, redeemedTicketId: stableId }
        : credit,
    );
  }
  await enqueueOutboxOnly({
    kind: "field_ticket_create",
    mutationId: createId(),
    eventId: EVENT_ID,
    ticketId: stableId,
    deviceId: storage.getDeviceId(),
    ticket: remapped,
    createdAt: new Date().toISOString(),
  });
  if (remapped.creditSourceId) {
    await enqueueOutboxOnly({
      kind: "redeem_deferred",
      mutationId: createId(),
      eventId: EVENT_ID,
      ticketId: stableId,
      creditSourceId: remapped.creditSourceId,
      couponIssued: remapped.couponIssued,
      deviceId: storage.getDeviceId(),
      createdAt: new Date().toISOString(),
    });
  }
  await persistTickets();
  if (draft.creditSourceId || remapped.creditSourceId) {
    await saveDeferredCreditsLocal(state.deferredCredits);
  }
  return remapped;
}

/** 檢查點同步前安全網：掃掉仍殘留的 local-*（不應是正常路徑）。 */
async function materializeLocalFieldDraftsForSync() {
  const drafts = state.tickets.filter((ticket) => isLocalDraftTicketId(ticket.id));
  for (const draft of drafts) {
    await materializeLocalFieldDraft(draft);
  }
}

function commitAllFieldPerformersFromDom() {
  const selects = [
    ...app.querySelectorAll<HTMLSelectElement>("select[data-field-performer]"),
  ];
  return Promise.all(
    selects.map((select) => {
      const ticketId = select.dataset.id || "";
      const unitId = select.dataset.unit || "";
      if (!ticketId || !unitId) return Promise.resolve();
      return commitFieldPerformerFromDom(ticketId, unitId);
    }),
  );
}

function checkpointSyncButtonLabel() {
  if (state.syncing) {
    return state.pendingOutbox ? `同步中（未上傳 ${state.pendingOutbox}）` : "同步中…";
  }
  return state.pendingOutbox ? `立即同步（未上傳 ${state.pendingOutbox}）` : "立即同步";
}

function paintCheckpointSyncButton() {
  const button = document.querySelector<HTMLButtonElement>("[data-action='checkpoint-sync']");
  if (!button) return;
  button.disabled = state.syncing;
  button.textContent = checkpointSyncButtonLabel();
}

async function checkpointSync(opts?: { quiet?: boolean }) {
  if (!isFieldMode(state.config.endpoint) || state.demo) return false;
  if (!state.online) {
    if (!opts?.quiet) showToast("目前離線，請到有網處再同步");
    return false;
  }
  if (state.syncing) return false;
  // Harvest DOM 指名 before syncing render wipes the selects.
  await commitAllFieldPerformersFromDom();
  state.syncing = true;
  state.syncError = null;
  render();
  try {
    await materializeLocalFieldDraftsForSync();
    const local = await loadLocalSnapshot();
    const outbox = local.outbox;
    const pushedCreateIds = new Set(
      outbox.filter((item) => item.kind === "field_ticket_create").map((item) => item.ticketId),
    );
    const beforeTickets = state.tickets;
    const ackedOutcomes = new Set(["applied", "duplicate", "superseded"]);
    const response = await runCheckpointSync(fieldConfig(), outbox, {
      onResult: async (item) => {
        if (!ackedOutcomes.has(item.outcome) || !item.mutationId) return;
        await removeOutbox([item.mutationId]);
        state.pendingOutbox = Math.max(0, state.pendingOutbox - 1);
        paintCheckpointSyncButton();
      },
    });
    const acked = (response.results || [])
      .filter((item) => item.outcome === "applied" || item.outcome === "duplicate" || item.outcome === "superseded")
      .map((item) => item.mutationId);
    if (acked.length) await removeOutbox(acked);
    const notes = (response.results || [])
      .filter((item) => item.outcome === "superseded")
      .map((item) => item.reason || "他機已先寫入");
    if (notes.length) state.autoNotes = notes.slice(0, 5);

    const hasSnapshot = Boolean(response.cursor) || (response.tickets || []).length > 0;
    if (!hasSnapshot) {
      const leftover = await loadLocalSnapshot();
      state.pendingOutbox = leftover.outbox.length;
      if (!opts?.quiet) {
        showToast(
          state.pendingOutbox
            ? `同步完成，仍有 ${state.pendingOutbox} 筆待處理`
            : "檢查點同步完成",
        );
      }
      return true;
    }

    await replaceLocalSnapshot({
      tickets: (response.tickets || []).map(normalizeTicket),
      feePayouts: response.feePayouts || [],
      cashCounts: (response.cashCounts || []).map((row) =>
        normalizeCashRecord({
          checkpoint: row.checkpoint,
          counts: normalizeCounts(row.counts),
          income: row.income,
          feeSnapshot: row.feeSnapshot,
          feeLocked: row.feeLocked,
          updatedAt: row.updatedAt,
          device: row.device,
        }),
      ),
      performerOptions: mergePerformerOptions(response.performerOptions, state.performerOptions),
      deferredCredits: response.deferredCredits || [],
      cursor: response.cursor,
      ssot: "db",
    });
    const next = await loadLocalSnapshot();
    const pendingTicketIds = new Set(
      next.outbox
        .map((item) => ("ticketId" in item ? item.ticketId : ""))
        .filter(Boolean),
    );
    const serverIds = new Set(next.tickets.map((ticket) => ticket.id));
    // Creates we pushed but snapshot omitted (error / race) must not vanish after remap.
    const retainTicketIds = new Set(
      [...pushedCreateIds].filter((id) => !serverIds.has(id)),
    );
    const merged = mergeTicketsAfterCheckpointPull({
      beforeTickets,
      serverTickets: next.tickets,
      pendingTicketIds,
      retainTicketIds,
    });
    const preservedCount = merged.length - next.tickets.length;
    state.tickets = merged.map(normalizeTicket);
    if (preservedCount > 0) await saveTicketsLocal(state.tickets);
    for (const id of Object.keys(state.fieldPendingCreateIds)) {
      if (serverIds.has(id) && !pendingTicketIds.has(id) && !retainTicketIds.has(id)) {
        delete state.fieldPendingCreateIds[id];
      }
    }
    state.feePayouts = next.feePayouts;
    state.cashCounts = next.cashCounts;
    state.deferredCredits = next.deferredCredits || [];
    state.performerOptions = mergePerformerOptions(next.performerOptions, state.performerOptions);
    storage.setPerformerOptions(state.performerOptions);
    state.pendingOutbox = next.outbox.length;
    state.lastSync = next.meta.lastSync;
    state.hasCachedSnapshot = true;
    state.demo = false;
    storage.setTickets(state.tickets);
    storage.setPerformerOptions(state.performerOptions);
    storage.setCashCounts(state.cashCounts);
    storage.setLastSync(state.lastSync);
    if (!opts?.quiet) {
      showToast(
        state.pendingOutbox
          ? `同步完成，仍有 ${state.pendingOutbox} 筆待處理`
          : "檢查點同步完成",
      );
    }
    return true;
  } catch (error) {
    const message = error instanceof Error ? error.message : "同步失敗";
    if (message === FIELD_TIMEOUT_MESSAGE && state.pendingOutbox === 0) {
      if (!opts?.quiet) showToast("檢查點同步完成");
      return true;
    }
    if (message === FIELD_TIMEOUT_MESSAGE) {
      if (!opts?.quiet) state.syncError = FIELD_TIMEOUT_MESSAGE;
      return false;
    }
    if (!opts?.quiet) showToast(message);
    return false;
  } finally {
    state.syncing = false;
    render();
  }
}

const appElement = document.querySelector<HTMLDivElement>("#app");
if (!appElement) throw new Error("App root not found");
const app = appElement;
let toastClearTimer = 0;

function escapeHtml(value: string | number | undefined): string {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function formatSyncTime(value: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("zh-TW", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function filteredTickets(): Ticket[] {
  return state.tickets.filter((ticket) => {
    if (isFieldTableTicket(ticket)) return false;
    if (!matchesTicket(ticket, state.query)) return false;
    const hasActiveSession = (ticket.units || []).some((unit) => unit.session === state.activeSession);
    const searchableCounterPayment = Boolean(state.query.trim()) && isCounterPaymentPending(ticket);
    if (!hasActiveSession && !searchableCounterPayment) return false;
    if (state.filter === "pending") return !isComplete(ticket);
    if (state.filter === "complete") return isComplete(ticket);
    if (state.filter === "payment") return paymentNeedsAttention(ticket);
    return true;
  });
}

/** Unredeemed deferred credits for checkin search; hide if that phone already has a checkin ticket. */
function filteredDeferredCredits(): DeferredCredit[] {
  const query = state.query.trim();
  if (!query) return [];
  const occupiedPhones = new Set(
    state.tickets
      .filter(
        (ticket) =>
          !isFieldTableTicket(ticket) &&
          ticket.kind !== "refund" &&
          ticket.opsStatus !== "refund",
      )
      .map((ticket) => phoneDigits(ticket.phone))
      .filter(Boolean),
  );
  return state.deferredCredits.filter((credit) => {
    if (!matchesDeferredCredit(credit, query)) return false;
    const phone = phoneDigits(credit.phone);
    if (phone && occupiedPhones.has(phone)) return false;
    return true;
  });
}

function fieldTableRows(): Array<{ ticket: Ticket; unit: TicketUnit; index: number }> {
  const rows: Array<{ ticket: Ticket; unit: TicketUnit; index: number }> = [];
  let index = 0;
  for (const ticket of state.tickets) {
    if (!isFieldTableTicket(ticket)) continue;
    if (ticket.kind === "refund" || ticket.opsStatus === "refund") continue;
    for (const unit of ticket.units || []) {
      if (unit.session !== state.activeSession) continue;
      index += 1;
      rows.push({ ticket, unit, index });
    }
  }
  return rows;
}

function fieldTypeLabel(ticket: Ticket): string {
  if (ticket.kind === "pr") return "公關";
  if (ticket.ticketType.includes("通票")) return "現場通票";
  if (ticket.ticketType.includes("晚場")) return "現場晚場";
  if (ticket.ticketType.includes("午場")) return "現場午場";
  return ticket.kind === "onsite" ? "現場" : ticket.ticketType;
}

function renderFieldTable(): string {
  const rows = fieldTableRows();
  const draftCount = state.tickets.filter(
    (ticket) =>
      isFieldTableTicket(ticket) &&
      (isLocalDraftTicketId(ticket.id) || state.fieldPendingCreateIds[ticket.id]),
  ).length;
  return `
    <section class="field-board">
      <div class="field-board-head">
        <h2>現場／公關</h2>
        <span>${rows.length} 張${draftCount ? ` · ${draftCount} 列本機` : ""}</span>
      </div>
      <section class="field-actions" aria-label="新增現場票">
        ${
          state.activeSession === "afternoon"
            ? `<button type="button" class="field-add" data-action="add-field" data-kind="onsite" data-package="afternoon">+現場午場</button>
               <button type="button" class="field-add" data-action="add-field" data-kind="onsite" data-package="both">+現場通票</button>
               <button type="button" class="field-add pr" data-action="add-field" data-kind="pr" data-package="afternoon">+公關票</button>`
            : `<button type="button" class="field-add" data-action="add-field" data-kind="onsite" data-package="evening">+現場晚場</button>
               <button type="button" class="field-add pr" data-action="add-field" data-kind="pr" data-package="evening">+公關票</button>`
        }
      </section>
      <div class="field-table-wrap">
        <table class="field-table">
          <thead>
            <tr>
              <th>#</th>
              <th>類型</th>
              <th>指名</th>
              <th>核票</th>
            </tr>
          </thead>
          <tbody>
            ${
              rows.length
                ? rows
                    .map(({ ticket, unit, index }) => {
                      const pending =
                        isLocalDraftTicketId(ticket.id) || Boolean(state.fieldPendingCreateIds[ticket.id]);
                      const canAssign = allowsPerformerAssignment(ticket);
                      const prompting =
                        state.forceCheckinPrompt?.ticketId === ticket.id &&
                        state.forceCheckinPrompt?.unitId === unit.id;
                      const checkCell = !allowsCheckIn(ticket)
                          ? `<span class="no-performer">不可核</span>`
                          : unit.checkedIn
                            ? `<button type="button" class="check-button is-checked" data-action="toggle-checkin" data-id="${escapeHtml(ticket.id)}" data-unit="${escapeHtml(unit.id)}" title="再按一次＝退款／取消核票">已核</button>`
                            : prompting
                              ? `<div class="force-checkin compact" role="group">
                                  <button type="button" class="force-yes" data-action="force-checkin-yes" data-id="${escapeHtml(ticket.id)}" data-unit="${escapeHtml(unit.id)}">強制</button>
                                  <button type="button" class="force-no" data-action="force-checkin-no">否</button>
                                </div>`
                              : `<button type="button" class="check-button" data-action="toggle-checkin" data-id="${escapeHtml(ticket.id)}" data-unit="${escapeHtml(unit.id)}" title="核票＝收款">核票</button>`;
                      const couponCell =
                        ticket.couponEligible && !ticket.couponIssued
                          ? `<button type="button" class="coupon-button compact" data-action="issue-coupon" data-id="${escapeHtml(ticket.id)}">發折價券</button>`
                          : ticket.couponIssued
                            ? `<span class="coupon-done">已發券</span>`
                            : "";
                      return `
                        <tr class="${unit.checkedIn ? "is-checked" : ""} ${pending ? "is-pending" : ""}">
                          <td class="field-num">${index}</td>
                          <td>${escapeHtml(fieldTypeLabel(ticket))}${pending ? ` <small class="no-performer">本機</small>` : ""}</td>
                          <td>
                            ${
                              canAssign
                                ? `<select data-field-performer data-id="${escapeHtml(ticket.id)}" data-unit="${escapeHtml(unit.id)}" aria-label="指名">
                                    ${renderPerformerOptions(state.activeSession, unit.performer || "尚未指名")}
                                  </select>`
                                : `<span class="no-performer">無指名</span>`
                            }
                            ${afternoonNamingHint(ticket, unit)}
                          </td>
                          <td><div class="checkin-actions">${couponCell}${checkCell}</div></td>
                        </tr>`;
                    })
                    .join("")
                : `<tr><td colspan="4" class="field-empty">尚無現場／公關票 · 用上方按鈕新增</td></tr>`
            }
          </tbody>
        </table>
      </div>
    </section>`;
}

function stats() {
  const allUnits = state.tickets.flatMap((ticket) => ticket.units || []);
  const activeUnits = allUnits.filter((unit) => unit.session === state.activeSession);
  const checked = activeUnits.filter((unit) => unit.checkedIn).length;
  return {
    orders: state.tickets.length,
    checked,
    admission: activeUnits.length,
    payment: state.tickets.filter(paymentNeedsAttention).length,
  };
}

function statusPill(ticket: Ticket): string {
  if (ticket.kind === "refund" || ticket.opsStatus === "refund") {
    return `<span class="status refund">已退票</span>`;
  }
  if (isComplete(ticket)) return `<span class="status complete">已完成</span>`;
  const partiallyChecked = ticket.sessions.some((session) => ticket.checkIns[session].count > 0);
  if (partiallyChecked) return `<span class="status partial">部分核票</span>`;
  return `<span class="status pending">待核票</span>`;
}

function renderPerformerOptions(session: Session, selected: string): string {
  const remote = state.performerOptions[session] || [];
  const fromTickets = optionsFromTickets(state.tickets, session);
  const choices = [
    ...new Set(["尚未指名", selected, ...remote, ...fromTickets].filter(Boolean)),
  ];
  return choices
    .map(
      (performer) =>
        `<option value="${escapeHtml(performer)}" ${performer === selected ? "selected" : ""}>${escapeHtml(performer)}</option>`,
    )
    .join("");
}

function mergePerformerOptions(
  incoming: Partial<Record<Session, string[]>> | undefined,
  fallback: Record<Session, string[]>,
): Record<Session, string[]> {
  const afternoon = incoming?.afternoon?.length
    ? incoming.afternoon
    : fallback.afternoon?.length
      ? fallback.afternoon
      : optionsFromTickets(state.tickets, "afternoon");
  const evening = incoming?.evening?.length
    ? incoming.evening
    : fallback.evening?.length
      ? fallback.evening
      : optionsFromTickets(state.tickets, "evening");
  return { afternoon, evening };
}

function afternoonNamingHint(ticket: Ticket, eveningUnit: { seat: number }): string {
  if (state.activeSession !== "evening") return "";
  const afternoonUnit = (ticket.units || []).find(
    (unit) => unit.session === "afternoon" && unit.seat === eveningUnit.seat,
  );
  if (!afternoonUnit) return "";
  return `<span class="afternoon-hint">${escapeHtml(afternoonNamingHintLabel(afternoonUnit.performer))}</span>`;
}

function unitRows(ticket: Ticket): string {
  if (!allowsCheckIn(ticket)) {
    return `<p class="refund-note">已退票｜可在票務狀態改回現場票</p>`;
  }
  const units = (ticket.units || []).filter((unit) => unit.session === state.activeSession);
  if (!units.length) {
    const amount = Math.abs(Number(ticket.settleDelta) || 0);
    if (ticket.settleStatus === "await_topup" && amount > 0) {
      return `<p class="refund-note">此場次沒有票券</p><div class="stepper checkin-actions"><button type="button" class="settle-btn" data-action="settle-topup" data-id="${escapeHtml(ticket.id)}" data-amount="${amount}">補款 ${amount} 元</button></div>`;
    }
    return `<p class="refund-note">此場次沒有票券</p>`;
  }
  const canAssign = allowsPerformerAssignment(ticket);
  const showCoupon = Boolean(ticket.couponEligible && !ticket.couponIssued);
  const showTopup = ticket.settleStatus === "await_topup";
  const showRefund = ticket.settleStatus === "await_refund";
  const settleAmount = Math.abs(Number(ticket.settleDelta) || 0);
  return units
    .map((unit, index) => {
      const prompting =
        state.forceCheckinPrompt?.ticketId === ticket.id &&
        state.forceCheckinPrompt?.unitId === unit.id;
      const checkBtn = unit.checkedIn
        ? `<button type="button" class="check-button is-checked" data-action="toggle-checkin" data-id="${escapeHtml(ticket.id)}" data-unit="${escapeHtml(unit.id)}">已核票 · 點此撤銷</button>`
        : prompting
          ? `<div class="force-checkin" role="group" aria-label="強制核票確認">
              <p>是否強制核票？</p>
              <div>
                <button type="button" class="force-yes" data-action="force-checkin-yes" data-id="${escapeHtml(ticket.id)}" data-unit="${escapeHtml(unit.id)}">是，強制核票</button>
                <button type="button" class="force-no" data-action="force-checkin-no">否</button>
              </div>
            </div>`
          : `<button type="button" class="check-button" data-action="toggle-checkin" data-id="${escapeHtml(ticket.id)}" data-unit="${escapeHtml(unit.id)}">核票</button>`;
      // 補款／退款 → 折價券 → 核票（多張票只在第一列顯示一次）
      const settleBtn =
        showTopup && index === 0
          ? `<button type="button" class="settle-btn" data-action="settle-topup" data-id="${escapeHtml(ticket.id)}" data-amount="${settleAmount}">補款 ${settleAmount} 元</button>`
          : showRefund && index === 0
            ? `<button type="button" class="settle-btn" data-action="settle-refund" data-id="${escapeHtml(ticket.id)}" data-amount="${settleAmount}">退款 $${settleAmount}</button>`
            : "";
      const couponBtn =
        showCoupon && index === 0
          ? `<button type="button" class="coupon-button" data-action="issue-coupon" data-id="${escapeHtml(ticket.id)}">發折價券</button>`
          : ticket.couponIssued && index === 0
            ? `<span class="coupon-done">折價券已發</span>`
            : "";
      return `
        <div class="session-row unit-row ${unit.checkedIn ? "checked" : ""} ${prompting ? "force-prompt" : ""}">
          <div class="unit-copy">
            <span class="session-name">${
              ticket.quantity > 1
                ? `第 ${unit.seat} 張`
                : ticket.sessions.length > 1
                  ? `${SESSION_LABELS[unit.session]}入場`
                  : "入場券"
            }</span>
            ${
              canAssign
                ? `<label>指名
              <select data-action="assign-performer" data-id="${escapeHtml(ticket.id)}" data-unit="${escapeHtml(unit.id)}">
                ${renderPerformerOptions(state.activeSession, unit.performer || "尚未指名")}
              </select>
            </label>`
                : `<span class="no-performer">無指名</span>`
            }
            ${afternoonNamingHint(ticket, unit)}
          </div>
          <div class="stepper checkin-actions">${settleBtn}${couponBtn}${checkBtn}</div>
        </div>`;
    })
    .join("");
}

function opsStatusSelect(ticket: Ticket): string {
  if (ticket.source === "field") {
    const label =
      ticket.kind === "pr"
        ? "公關票"
        : ticket.opsStatus === "onsite_both"
          ? OPS_STATUS_LABELS.onsite_both
          : ticket.opsStatus === "onsite_evening"
            ? OPS_STATUS_LABELS.onsite_evening
            : OPS_STATUS_LABELS.onsite_afternoon;
    return `<p class="refund-note">現場／公關：用核票＝收款，再按一次＝退款（不做退票註記）· ${escapeHtml(label)}</p>`;
  }
  const current = ticket.opsStatus || "";
  const original = ticket.originalTicketType || ticket.ticketType;
  if (current.startsWith("onsite_")) {
    const amount = Math.abs(Number(ticket.settleDelta) || 0);
    const paymentCopy = amount
      ? ticket.settleStatus === "topup_paid"
        ? `｜已補款 ${amount} 元`
        : `｜應補 ${amount} 元`
      : "";
    return `<p class="refund-note">已改為 ${escapeHtml(OPS_STATUS_LABELS[current as Exclude<TicketOpsStatus, "" | "refund">])}${paymentCopy}</p>`;
  }
  const plans = (["onsite_afternoon", "onsite_evening", "onsite_both"] as const)
    .map((status) => onsiteTicketPlan(ticket, status))
    .filter((plan): plan is NonNullable<typeof plan> => Boolean(plan));
  return `
    <label class="ops-status">票務狀態
      <select data-action="update-ops" data-id="${escapeHtml(ticket.id)}">
        <option value="" ${current === "" ? "selected" : ""}>原票種（${escapeHtml(original)}）</option>
        ${plans.map((plan) => `<option value="${plan.opsStatus}">改為${OPS_STATUS_LABELS[plan.opsStatus]}（補款 ${plan.settleDelta} 元）</option>`).join("")}
        <option value="refund" ${current === "refund" ? "selected" : ""}>${OPS_STATUS_LABELS.refund}</option>
      </select>
    </label>`;
}

function phoneTailForSummary(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  if (!digits) return "—";
  return digits.slice(-3);
}

function settleBanner(ticket: Ticket): string {
  const prior = Number(ticket.priorCredit) || 0;
  if (!prior && !ticket.settleStatus) return "";
  const list = ticket.listPrice != null ? Number(ticket.listPrice) : null;
  const delta = Number(ticket.settleDelta) || 0;
  const status = ticket.settleStatus || "";
  let deltaLine = "";
  if (status === "await_topup") deltaLine = `｜<strong>應補 $${delta}</strong>`;
  else if (status === "topup_paid") deltaLine = `｜補款已到 $${delta}`;
  else if (status === "await_refund") deltaLine = `｜<strong>現場退 $${Math.abs(delta)}</strong>`;
  else if (status === "refunded") deltaLine = `｜已退 $${Math.abs(delta)}`;
  else if (status === "clear") deltaLine = "｜已結清";
  const coupon =
    ticket.couponEligible && !ticket.couponIssued
      ? "｜可發$50 折價券"
      : ticket.couponIssued
        ? "｜折價券已發"
        : "";
  const sourceCopy = prior || ticket.creditSourceId ? `延用 $${prior}` : "票種變更";
  return `<div class="settle-banner">${sourceCopy}${list != null ? `｜本場次 $${list}` : ""}${deltaLine}${coupon}</div>`;
}

function ticketCard(ticket: Ticket): string {
  const units = (ticket.units || []).filter((unit) => unit.session === state.activeSession);
  const checked = units.filter((unit) => unit.checkedIn).length;
  const open = state.selectedId === ticket.id;
  const kindBadge =
    ticket.kind === "onsite"
      ? `<span class="kind-badge onsite">現場</span>`
      : ticket.kind === "pr"
        ? `<span class="kind-badge pr">公關</span>`
        : ticket.kind === "refund"
          ? `<span class="kind-badge refund">退票</span>`
          : "";
  const typeShort =
    ticket.ticketType.length > 14 ? `${ticket.ticketType.slice(0, 13)}…` : ticket.ticketType;
  return `
    <article class="ticket-card ${isComplete(ticket) ? "done" : ""} ${open ? "is-open" : ""}" data-ticket="${ticket.id}">
      <button type="button" class="ticket-summary" data-action="select" data-id="${ticket.id}" aria-expanded="${open ? "true" : "false"}">
        <span class="ticket-line-main">
          <span class="ticket-name">${escapeHtml(displayTicketName(ticket))}${kindBadge}</span>
          <span class="ticket-phone">${escapeHtml(phoneTailForSummary(ticket.phone))}</span>
          <span class="ticket-type-short" title="${escapeHtml(ticket.ticketType)}">${escapeHtml(typeShort)}</span>
        </span>
        <span class="summary-right">
          <span class="ticket-progress" title="${SESSION_LABELS[state.activeSession]}已核">${checked}/${units.length}</span>
          ${statusPill(ticket)}
          <span class="chevron" aria-hidden="true">${open ? "▾" : "›"}</span>
        </span>
      </button>
      <div class="ticket-detail ${open ? "open" : ""}">
        ${settleBanner(ticket)}
        ${paymentNeedsAttention(ticket) ? `<div class="warning">付款待確認｜${escapeHtml(ticket.paymentMethod)}</div>` : ""}
        <p class="ticket-detail-meta">${escapeHtml(ticket.ticketType)} · ${ticket.quantity} 張 · ${SESSION_LABELS[state.activeSession]} ${checked}/${units.length} 已核</p>
        <dl>
          <div><dt>電話</dt><dd>${escapeHtml(displayPhone(ticket.phone))}</dd></div>
          <div><dt>電子郵件</dt><dd>${escapeHtml(ticket.email || "—")}</dd></div>
          <div><dt>付款方式</dt><dd>${escapeHtml(ticket.paymentMethod || "—")}</dd></div>
          ${ticket.transferSuffix ? `<div><dt>末五碼</dt><dd>${escapeHtml(ticket.transferSuffix)}</dd></div>` : ""}
        </dl>
        ${opsStatusSelect(ticket)}
        <div class="sessions">${unitRows(ticket)}</div>
      </div>
    </article>`;
}

function conflictCard(conflict: SyncConflict): string {
  const ticket =
    conflict.ticket || state.tickets.find((item) => item.id === conflict.mutation.ticketId);
  const action =
    conflict.mutation.kind === "assignment"
      ? `重新指名為 ${conflict.mutation.performer}`
      : conflict.mutation.delta === 1
        ? "核票"
        : "撤銷核票";
  const actual =
    conflict.actualCount === undefined ? "" : `；伺服器目前為 ${conflict.actualCount} 人`;

  return `
    <article class="conflict-card">
      <strong>${escapeHtml(ticket?.name || conflict.mutation.ticketId)}</strong>
      <p>${SESSION_LABELS[conflict.mutation.session]}｜原操作：${action}${escapeHtml(actual)}</p>
      <small>${escapeHtml(conflict.message)}</small>
      <div>
        <button data-action="dismiss-conflict" data-conflict="${escapeHtml(conflict.id)}">接受伺服器狀態</button>
        <button class="primary" data-action="retry-conflict" data-conflict="${escapeHtml(conflict.id)}">重新執行原操作</button>
      </div>
    </article>`;
}

function renderFees() {
  const afternoonRows = buildAfternoonFeeRows(state.tickets, state.feePayouts);
  const eveningRows = buildEveningFeeRows(state.tickets, state.feePayouts);
  const summaryRows = buildFeeSummary(state.tickets, state.feePayouts);
  const afternoonTotalPaid = afternoonRows.reduce((sum, row) => sum + row.paid, 0);
  const eveningTotalPaid = eveningRows.reduce((sum, row) => sum + row.paid, 0);
  const grandPaid = summaryRows.reduce((sum, row) => sum + row.totalPaid, 0);

  app.innerHTML = `
    <header class="topbar">
      <div>
        <p class="eyebrow">IDOL ISLAND · 08.16</p>
        <h1>指名費統計</h1>
      </div>
      <button type="button" class="round-button" data-action="view-checkin" aria-label="回核票">←</button>
    </header>
    <main class="fees-main">
      <p class="fees-note">只計已核票指名 · 每人次 ${NOMINATION_FEE_PER_CHECKIN} 元 · 實給會寫回試算表「_指名費」</p>

      <section class="fee-block">
        <h2>午場</h2>
        <div class="fee-table-wrap">
          <table class="fee-table">
            <thead>
              <tr><th>團體</th><th>指名</th><th>應給</th><th>實給</th><th></th></tr>
            </thead>
            <tbody>
              ${
                afternoonRows.length
                  ? afternoonRows
                      .map(
                        (row) => `
                  <tr>
                    <td>${escapeHtml(row.performer)}</td>
                    <td>${row.count}</td>
                    <td>${row.owed}</td>
                    <td>
                      <input class="fee-input" type="number" min="0" step="100" inputmode="numeric"
                        data-fee-session="afternoon" data-fee-performer="${escapeHtml(row.performer)}"
                        value="${row.paid}" />
                    </td>
                    <td>
                      <button type="button" class="fee-save" data-action="save-fee"
                        data-session="afternoon" data-performer="${escapeHtml(row.performer)}">
                        ${state.feeQueue.some((item) => item.session === "afternoon" && item.performer === row.performer) ? "同步中" : "存"}
                      </button>
                    </td>
                  </tr>`,
                      )
                      .join("")
                  : `<tr><td colspan="5">尚無午場已核票指名</td></tr>`
              }
            </tbody>
            <tfoot><tr><td>合計實給</td><td></td><td></td><td colspan="2">${afternoonTotalPaid}</td></tr></tfoot>
          </table>
        </div>
      </section>

      <section class="fee-block">
        <h2>晚場</h2>
        <div class="fee-table-wrap">
          <table class="fee-table">
            <thead>
              <tr>
                <th>團體</th><th>晚指名</th><th>午指名</th><th>午未給</th><th>晚應給</th><th>合計應給</th><th>實給</th><th></th>
              </tr>
            </thead>
            <tbody>
              ${
                eveningRows.length
                  ? eveningRows
                      .map(
                        (row) => `
                  <tr>
                    <td>${escapeHtml(row.performer)}</td>
                    <td>${row.eveningCount}</td>
                    <td>${row.afternoonCount}</td>
                    <td>${row.unpaidAfternoon}</td>
                    <td>${row.eveningOwed}</td>
                    <td>${row.totalOwed}</td>
                    <td>
                      <input class="fee-input" type="number" min="0" step="100" inputmode="numeric"
                        data-fee-session="evening" data-fee-performer="${escapeHtml(row.performer)}"
                        value="${row.paid}" />
                    </td>
                    <td>
                      <button type="button" class="fee-save" data-action="save-fee"
                        data-session="evening" data-performer="${escapeHtml(row.performer)}">
                        ${state.feeQueue.some((item) => item.session === "evening" && item.performer === row.performer) ? "同步中" : "存"}
                      </button>
                    </td>
                  </tr>`,
                      )
                      .join("")
                  : `<tr><td colspan="8">尚無晚場可發放資料</td></tr>`
              }
            </tbody>
            <tfoot><tr><td>合計實給</td><td colspan="5"></td><td colspan="2">${eveningTotalPaid}</td></tr></tfoot>
          </table>
        </div>
      </section>

      <section class="fee-block">
        <h2>發放總表</h2>
        <div class="fee-table-wrap">
          <table class="fee-table">
            <thead>
              <tr><th>團體</th><th>午場實給</th><th>晚場實給</th><th>合計</th></tr>
            </thead>
            <tbody>
              ${
                summaryRows.length
                  ? summaryRows
                      .map(
                        (row) => `
                  <tr>
                    <td>${escapeHtml(row.performer)}</td>
                    <td>${row.afternoonPaid}</td>
                    <td>${row.eveningPaid}</td>
                    <td>${row.totalPaid}</td>
                  </tr>`,
                      )
                      .join("")
                  : `<tr><td colspan="4">尚無實給紀錄</td></tr>`
              }
            </tbody>
            <tfoot><tr><td>全部實給</td><td colspan="2"></td><td>${grandPaid}</td></tr></tfoot>
          </table>
        </div>
      </section>
    </main>
    ${state.toast ? `<div class="toast">${escapeHtml(state.toast)}</div>` : ""}
  `;
}

function cashSection(checkpoint: CashCheckpoint): string {
  const record = state.cashCounts[checkpoint];
  const prev = previousCheckpoint(checkpoint);
  const previousBalance = prev ? state.cashCounts[prev].balance : 0;
  const balanceDelta = prev ? computeBalanceDelta(record.balance, previousBalance) : 0;
  const doorIncome = checkpoint === "opening" ? 0 : computeDoorIncome(state.tickets, checkpoint);
  const variance = prev
    ? computeVariance({
        balanceDelta,
        income: doorIncome,
        feeSnapshot: record.feeSnapshot,
      })
    : 0;
  const pending = state.cashQueue.includes(checkpoint);
  return `
    <section class="cash-block" data-checkpoint="${checkpoint}">
      <div class="cash-block-head">
        <div>
          <h2>${CASH_LABELS[checkpoint]}</h2>
          <p class="cash-balance">餘額 <strong>${record.balance}</strong></p>
        </div>
        ${pending ? `<span class="cash-pending">同步中</span>` : ""}
      </div>
      <div class="cash-denom-list">
        ${DENOMINATIONS.map((denomination) => {
          const qty = record.counts[denomination] || 0;
          return `
            <label class="cash-denom-row">
              <span class="cash-face">${denomination}</span>
              <input class="cash-qty" type="number" min="0" step="1" inputmode="numeric"
                data-cash-qty="${checkpoint}" data-denomination="${denomination}"
                value="${qty}" />
              <span class="cash-subtotal">${denomination * qty}</span>
            </label>`;
        }).join("")}
      </div>
      ${
        prev
          ? `
        <div class="cash-recon">
          <div><span>餘額差</span><strong>${balanceDelta}</strong></div>
          <div>
            <span>收入(+)（自動）</span>
            <strong class="cash-income-value">${doorIncome}</strong>
            <small>現場票＋延用補−退</small>
          </div>
          <div>
            <span>指名費(-)</span>
            <strong>${record.feeSnapshot}</strong>
            ${record.feeLocked ? `<small>已鎖定</small>` : `<small>存檔時鎖定</small>`}
          </div>
          <div class="cash-variance ${variance === 0 ? "is-flat" : "is-off"}">
            <span>差額</span><strong>${variance}</strong>
            <small>${variance === 0 ? "帳平" : "帳不平"}</small>
          </div>
        </div>`
          : ""
      }
      <div class="cash-actions">
        <button type="button" class="fee-save" data-action="save-cash" data-checkpoint="${checkpoint}">存</button>
        ${
          prev && record.feeLocked
            ? `<button type="button" class="text-button" data-action="refresh-cash-fee" data-checkpoint="${checkpoint}">重抓指名費</button>`
            : ""
        }
      </div>
    </section>`;
}

function renderCash() {
  app.innerHTML = `
    <header class="topbar">
      <div>
        <p class="eyebrow">IDOL ISLAND · 08.16</p>
        <h1>錢箱盤點</h1>
      </div>
      <button type="button" class="round-button" data-action="view-checkin" aria-label="回核票">←</button>
    </header>
    <main class="cash-main">
      <p class="fees-note">面額點完按「存」。收入＝現場票核票收款＋延用補款−延用退款（自動；不含匯款確認補款）。午／晚場第一次存會鎖定當下指名費實給合計。</p>
      ${cashSection("opening")}
      ${cashSection("afternoon")}
      ${cashSection("evening")}
    </main>
    ${state.toast ? `<div class="toast">${escapeHtml(state.toast)}</div>` : ""}
  `;
}

function render() {
  const view = state.view;
  if (view === "fees") {
    renderFees();
    return;
  }
  if (view === "cash") {
    renderCash();
    return;
  }
  const currentStats = stats();
  const tickets = filteredTickets();
  const creditHits = view === "checkin" ? filteredDeferredCredits() : [];
  app.innerHTML = `
    <header class="topbar">
      <div>
        <p class="eyebrow">IDOL ISLAND · 08.16</p>
        <h1>${view === "field" ? "現場／公關" : "早鳥／預售"}</h1>
      </div>
      <div class="topbar-actions">
        <button type="button" class="text-button ${view === "checkin" ? "active" : ""}" data-action="view-checkin" aria-label="早鳥／預售"><span class="nav-full">早鳥／預售</span><span class="nav-short" aria-hidden="true">早</span></button>
        <button type="button" class="text-button ${view === "field" ? "active" : ""}" data-action="view-field" aria-label="現場／公關"><span class="nav-full">現場／公關</span><span class="nav-short" aria-hidden="true">現</span></button>
        <button type="button" class="text-button" data-action="view-cash" aria-label="錢箱"><span class="nav-full">錢箱</span><span class="nav-short" aria-hidden="true">錢</span></button>
        <button type="button" class="text-button" data-action="view-fees" aria-label="指名費"><span class="nav-full">指名費</span><span class="nav-short" aria-hidden="true">指</span></button>
        <button type="button" class="round-button" data-action="settings" aria-label="設定">⚙</button>
      </div>
    </header>

    <main>
      <section class="connection ${state.online ? "online" : "offline"}">
        <div class="connection-row">
        <span class="connection-dot"></span>
        <span>${
          state.demo
            ? "展示資料"
            : !state.online
              ? "離線帳簿"
            : state.initialLoading && !state.hasCachedSnapshot
              ? "首次同步中"
              : state.syncing
                ? "檢查點同步中"
                : isFieldMode(state.config.endpoint)
                  ? "本機帳簿"
                  : "已連線"
        }</span>
        ${
          isFieldMode(state.config.endpoint) && !state.demo
            ? `<button data-action="checkpoint-sync" ${state.syncing ? "disabled" : ""}>${checkpointSyncButtonLabel()}</button>`
            : !state.demo && state.queue.length
              ? `<button data-action="sync">${state.queue.length} 筆待同步</button>`
              : ""
        }
        ${state.lastSync ? `<time>同步 ${escapeHtml(formatSyncTime(state.lastSync))}</time>` : ""}
        </div>
        ${state.syncError ? `<p class="sync-error">${escapeHtml(state.syncError)}</p>` : ""}
      </section>

      ${
        state.autoNotes.length
          ? `<section class="conflict-banner">
              <span>✓</span>
              <div><strong>已自動對齊</strong><small>${escapeHtml(state.autoNotes.join("；"))}</small></div>
              <button data-action="clear-notes">知道了</button>
            </section>`
          : ""
      }

      ${
        !isFieldMode(state.config.endpoint) && state.conflicts.length
          ? `<section class="conflict-banner">
              <span>!</span>
              <div><strong>${state.conflicts.length} 筆同步衝突</strong><small>伺服器沒有覆蓋資料，請人工確認</small></div>
              <button data-action="open-conflicts">查看</button>
            </section>`
          : ""
      }

      <section class="stats">
        <button data-filter="all"><span>${currentStats.orders}</span>訂單</button>
        <button data-filter="complete"><span>${currentStats.checked}<small>/${currentStats.admission}</small></span>入場人次</button>
        <button data-filter="payment"><span>${currentStats.payment}</span>付款待確認</button>
      </section>

      <nav class="session-switcher" aria-label="目前核票場次">
        ${(["afternoon", "evening"] as Session[])
          .map(
            (session) =>
              `<button data-action="session" data-session="${session}" class="${state.activeSession === session ? "active" : ""}">${SESSION_LABELS[session]}核票</button>`,
          )
          .join("")}
      </nav>

      ${
        view === "field"
          ? ""
          : `<section class="search-panel">
        <div class="search">
          <span class="search-icon" aria-hidden="true">⌕</span>
          <input id="search-phone" class="search-input" type="text" inputmode="numeric"
            autocomplete="off" enterkeyhint="search"
            placeholder="${escapeHtml(searchPlaceholder("phone"))}"
            value="${escapeHtml(state.query)}"
            ${state.searchMode !== "phone" ? "hidden" : ""} />
          <input id="search-name" class="search-input" type="text" inputmode="text" lang="zh-Hant"
            autocapitalize="words" autocomplete="off" enterkeyhint="search"
            placeholder="${escapeHtml(searchPlaceholder("name"))}"
            value="${escapeHtml(state.query)}"
            ${state.searchMode !== "name" ? "hidden" : ""} />
          <button type="button" class="search-clear" data-action="clear" aria-label="清除"
            ${state.query ? "" : "hidden"}>×</button>
          <span class="search-mode" role="group" aria-label="搜尋方式">
            <button type="button" data-action="search-mode" data-mode="phone"
              class="${state.searchMode === "phone" ? "active" : ""}" aria-pressed="${state.searchMode === "phone"}">電話</button>
            <button type="button" data-action="search-mode" data-mode="name"
              class="${state.searchMode === "name" ? "active" : ""}" aria-pressed="${state.searchMode === "name"}">名稱</button>
          </span>
        </div>
      </section>`
      }

      ${
        view === "field"
          ? renderFieldTable()
          : `
      <nav class="filters" aria-label="票券篩選">
        ${(["all", "pending", "complete", "payment"] as Filter[])
          .map((filter) => {
            const labels: Record<Filter, string> = {
              all: "全部",
              pending: "待核票",
              complete: "已完成",
              payment: "付款注意",
            };
            return `<button data-filter="${filter}" class="${state.filter === filter ? "active" : ""}">${labels[filter]}</button>`;
          })
          .join("")}
      </nav>

      <section class="result-head">
        <h2>${state.query ? `搜尋結果` : "預售／早鳥名單"}</h2>
        <span>${tickets.length} 筆${creditHits.length ? ` · ${creditHits.length} 延用` : ""}</span>
      </section>
      <section class="ticket-list">
        ${renderCheckinListBody(tickets, creditHits)}
      </section>`
      }
    </main>

    <dialog id="settings-dialog">
      <form method="dialog" id="settings-form">
        <div class="dialog-head"><div><p class="eyebrow">資料連線</p><h2>後端設定</h2></div><button value="cancel" aria-label="關閉">×</button></div>
        <p class="dialog-copy">場日請用 <code>/api/field</code>（Supabase）。Sheet 僅供匯入前作業。</p>
        <label>API 網址<input name="endpoint" type="text" placeholder="/api/field" value="${escapeHtml(state.config.endpoint)}" /></label>
        <label>Field Token（可選）<input name="token" type="password" autocomplete="off" value="${escapeHtml(state.config.token)}" /></label>
        <label>本機角色
          <select name="deviceRole">
            ${(["general", "checkin", "field_sales", "fees", "cash"] as DeviceRole[])
              .map(
                (role) =>
                  `<option value="${role}" ${state.deviceRole === role ? "selected" : ""}>${
                    {
                      general: "通用（可寫全部）",
                      checkin: "核票",
                      field_sales: "現場票",
                      fees: "指名費主責",
                      cash: "錢箱主責",
                    }[role]
                  }</option>`,
              )
              .join("")}
          </select>
        </label>
        <p class="dialog-copy">裝置 ${escapeHtml(storage.getDeviceId())} · 未上傳 ${state.pendingOutbox}</p>
        <div class="dialog-actions">
          <button value="demo" data-action="demo">使用展示資料</button>
          <button value="save" class="primary" data-action="save-settings">儲存</button>
        </div>
      </form>
    </dialog>

    <dialog id="conflicts-dialog">
      <div class="dialog-head"><div><p class="eyebrow">需要人工確認</p><h2>離線同步衝突</h2></div><button data-action="close-conflicts" aria-label="關閉">×</button></div>
      <p class="dialog-copy">另一台裝置已先改變同一張票。系統已採用伺服器狀態，沒有靜默覆蓋；請逐筆決定是否重新執行原操作。</p>
      <section class="conflict-list">${state.conflicts.map(conflictCard).join("")}</section>
    </dialog>
    ${
      state.initialLoading && !state.hasCachedSnapshot
        ? isFieldMode(state.config.endpoint)
          ? `<div class="loading"><span></span><p>同步場日名單</p><small>從本機場日 API 下載後可離線核票</small></div>`
          : `<div class="loading"><span></span><p>首次下載核票名單</p><small>完成後將儲存在此裝置，可離線使用</small></div>`
        : ""
    }
    ${state.toast ? `<div class="toast">${escapeHtml(state.toast)}</div>` : ""}
  `;
}

function showToast(message: string) {
  state.toast = message;
  // 不要整頁 render：會把開著的指名下拉選單拆掉
  let toast = app.querySelector<HTMLElement>(".toast");
  if (!toast) {
    toast = document.createElement("div");
    toast.className = "toast";
    app.appendChild(toast);
  }
  toast.textContent = message;
  window.clearTimeout(toastClearTimer);
  toastClearTimer = window.setTimeout(() => {
    state.toast = "";
    app.querySelector(".toast")?.remove();
  }, 2400);
}

function applyMutationLocally(mutation: SyncMutation) {
  const ticket = state.tickets.find((item) => item.id === mutation.ticketId);
  if (!ticket) return;
  const unit = (ticket.units || []).find((item) => item.id === mutation.unitId);
  if (!unit) return;
  if (mutation.kind === "assignment") unit.performer = mutation.performer;
  else unit.checkedIn = mutation.delta === 1;
  unit.updatedAt = mutation.createdAt;
  unit.device = mutation.deviceId;
  ticket.checkIns[mutation.session].count = (ticket.units || []).filter(
    (item) => item.session === mutation.session && item.checkedIn,
  ).length;
  storage.setTickets(state.tickets);
}

function buildLocalFieldTicket(kind: "onsite" | "pr", pkg: "afternoon" | "evening" | "both"): Ticket {
  const id = `local-${createId()}`;
  const sessions: Session[] =
    pkg === "both" ? ["afternoon", "evening"] : pkg === "evening" ? ["evening"] : ["afternoon"];
  const opsStatus =
    kind === "pr"
      ? ""
      : pkg === "both"
        ? "onsite_both"
        : pkg === "evening"
          ? "onsite_evening"
          : "onsite_afternoon";
  const ticketType =
    kind === "pr"
      ? pkg === "evening"
        ? "公關票（晚場）"
        : "公關票（午場）"
      : ticketTypeFromOpsStatus(opsStatus, "");
  const units: TicketUnit[] = sessions.map((session) => ({
    id: `${id}:1:${session}`,
    seat: 1,
    session,
    performer: "尚未指名",
    checkedIn: false,
  }));
  return {
    id,
    name: kind === "pr" ? "公關票" : "現場票",
    phone: "",
    email: "",
    ticketType,
    originalTicketType: ticketType,
    quantity: 1,
    sessions,
    afternoonPerformer: "",
    eveningPerformer: "",
    paymentMethod: kind === "pr" ? "公關免付" : "現場現金",
    transferSuffix: "",
    paymentRecorded: true,
    units,
    checkIns: {
      afternoon: { count: 0 },
      evening: { count: 0 },
    },
    source: "field",
    kind,
    opsStatus,
    listPrice: kind === "onsite" ? ONSITE_DOOR_PRICES[pkg] : 0,
  };
}

/** 7/11 延用兌換：具名票走早鳥／預售核票列表（source=form），不是現場匿名表。 */
function buildDeferredRedeemTicket(
  credit: { name: string; phone: string; email: string; gmail?: string },
  pkg: "afternoon" | "evening" | "both",
): Ticket {
  const base = buildLocalFieldTicket("onsite", pkg);
  const opsStatus =
    pkg === "both" ? "onsite_both" : pkg === "evening" ? "onsite_evening" : "onsite_afternoon";
  const ticketType = ticketTypeFromOpsStatus(opsStatus, "");
  return {
    ...base,
    name: credit.name || base.name,
    phone: credit.phone || "",
    email: credit.email || credit.gmail || "",
    ticketType,
    originalTicketType: ticketType,
    source: "form",
    kind: "onsite",
    opsStatus,
  };
}

function packageFromTicket(ticket: Ticket): "afternoon" | "evening" | "both" {
  if (ticket.sessions.includes("afternoon") && ticket.sessions.includes("evening")) return "both";
  if (ticket.sessions.includes("evening")) return "evening";
  return "afternoon";
}

function remapTicketId(ticket: Ticket, newId: string): Ticket {
  return normalizeTicket({
    ...ticket,
    id: newId,
    units: (ticket.units || []).map((unit) => ({
      ...unit,
      id: `${newId}:${unit.seat}:${unit.session}`,
    })),
  });
}

/** Outbox only — seats already exist locally; do not re-apply. */
async function enqueueOutboxOnly(mutation: FieldMutation) {
  void fieldMutationRequiresOutbox(mutation.kind);
  await appendOutbox(mutation);
  state.pendingOutbox += 1;
}

async function flushPendingExtraSeats(ticketId: string) {
  const extra = state.fieldPendingExtraSeats[ticketId] || 0;
  if (!extra) return;
  delete state.fieldPendingExtraSeats[ticketId];
  if (isFieldMode(state.config.endpoint) && !state.demo) {
    await enqueueOutboxOnly({
      kind: "field_ticket_add_seat",
      mutationId: createId(),
      eventId: EVENT_ID,
      ticketId,
      seats: extra,
      deviceId: storage.getDeviceId(),
      createdAt: new Date().toISOString(),
    });
    return;
  }
  if (!state.demo && state.config.endpoint) {
    for (let i = 0; i < extra; i += 1) {
      const response = await addFieldTicketSeat(state.config, {
        ticketId,
        deviceId: storage.getDeviceId(),
      });
      if (response.ticket) {
        const next = normalizeTicket(response.ticket);
        state.tickets = state.tickets.map((item) => (item.id === ticketId ? next : item));
      }
    }
    await persistTickets();
  }
}

/**
 * 現場列：場日模式若仍有 local-* orphan 則立刻穩定化＋outbox；非場日路徑在核票時才建立。
 * 回傳可能已 remap 的票與對應 unitId。
 */
async function flushFieldTicketBeforeCheckin(
  ticket: Ticket,
  unit: TicketUnit,
): Promise<{ ticket: Ticket; unitId: string }> {
  const seat = unit.seat;
  const session = unit.session;

  if (isLocalDraftTicketId(ticket.id)) {
    if (isFieldMode(state.config.endpoint) && !state.demo) {
      const remapped = await materializeLocalFieldDraft(ticket);
      const nextUnit = (remapped.units || []).find((item) => item.seat === seat && item.session === session);
      return { ticket: remapped, unitId: nextUnit?.id || `${remapped.id}:${seat}:${session}` };
    }

    if (!state.demo && state.config.endpoint) {
      const response = await createFieldTicket(state.config, {
        kind: ticket.kind === "pr" ? "pr" : "onsite",
        package: packageFromTicket(ticket),
        deviceId: storage.getDeviceId(),
      });
      if (!response.ticket) throw new Error("新增現場票失敗");
      let server = normalizeTicket(response.ticket);
      while (server.quantity < ticket.quantity) {
        const seatResponse = await addFieldTicketSeat(state.config, {
          ticketId: server.id,
          deviceId: storage.getDeviceId(),
        });
        if (!seatResponse.ticket) break;
        server = normalizeTicket(seatResponse.ticket);
      }
      const merged = normalizeTicket({
        ...server,
        units: (server.units || []).map((serverUnit) => {
          const localUnit = (ticket.units || []).find(
            (item) => item.seat === serverUnit.seat && item.session === serverUnit.session,
          );
          return localUnit
            ? { ...serverUnit, performer: localUnit.performer, checkedIn: false }
            : serverUnit;
        }),
      });
      state.tickets = state.tickets.map((item) => (item.id === ticket.id ? merged : item));
      delete state.fieldPendingExtraSeats[ticket.id];
      await persistTickets();
      for (const mergedUnit of merged.units || []) {
        if (!mergedUnit.performer || mergedUnit.performer === "尚未指名") continue;
        state.queue.push({
          kind: "assignment",
          mutationId: createId(),
          eventId: EVENT_ID,
          ticketId: merged.id,
          unitId: mergedUnit.id,
          session: mergedUnit.session,
          performer: mergedUnit.performer,
          expectedPerformer: "尚未指名",
          deviceId: storage.getDeviceId(),
          createdAt: new Date().toISOString(),
        });
      }
      storage.setQueue(state.queue);
      const nextUnit = (merged.units || []).find((item) => item.seat === seat && item.session === session);
      return { ticket: merged, unitId: nextUnit?.id || unit.id };
    }
  }

  await flushPendingExtraSeats(ticket.id);
  const current = state.tickets.find((item) => item.id === ticket.id) || ticket;
  const nextUnit = (current.units || []).find((item) => item.seat === seat && item.session === session);
  return { ticket: current, unitId: nextUnit?.id || unit.id };
}

async function addFieldTicket(kind: "onsite" | "pr", pkg: "afternoon" | "evening" | "both") {
  if (state.demo || !state.config.endpoint) {
    showToast("展示模式無法新增現場票");
    return;
  }
  if (!canSellField(state.deviceRole)) {
    showToast("此裝置角色不能賣現場票");
    return;
  }
  const local = buildLocalFieldTicket(kind, pkg);
  // 場日：立刻穩定 id + outbox create（允許部分指名）；同步按鈕顯示未上傳。
  if (isFieldMode(state.config.endpoint) && !state.demo) {
    const stableId = `${storage.getDeviceId()}-${createId().slice(0, 8)}`;
    const ticket = remapTicketId(local, stableId);
    state.tickets = [ticket, ...state.tickets];
    state.fieldPendingCreateIds[stableId] = true;
    await enqueueOutboxOnly({
      kind: "field_ticket_create",
      mutationId: createId(),
      eventId: EVENT_ID,
      ticketId: stableId,
      deviceId: storage.getDeviceId(),
      ticket,
      createdAt: new Date().toISOString(),
    });
    await persistTickets();
    render();
    return;
  }
  state.tickets = [local, ...state.tickets];
  void persistTickets();
  render();
}

function deferredCreditHitCard(credit: DeferredCredit): string {
  return `<article class="credit-hit" aria-label="7/11 延用額度">
    <div class="credit-hit-main">
      <span class="credit-hit-badge">7/11 延用</span>
      <strong>${escapeHtml(credit.name || "—")}</strong>
      <span>· ${escapeHtml(displayPhone(credit.phone))} · 額度 $${credit.priorCredit}${
        couponEligibleOnRedeem(credit) && !credit.couponIssued ? " · 可發$50 折價券" : ""
      }</span>
    </div>
    <p class="credit-hit-note">選套票後才會顯示應補／應退</p>
    <div class="credit-hit-actions">
      ${deferredRedeemPackages(state.activeSession)
        .map(
          (pkg) =>
            `<button type="button" class="field-add" data-action="credit-redeem" data-credit="${escapeHtml(credit.id)}" data-package="${pkg}">${ONSITE_PACKAGE_LABELS[pkg]}</button>`,
        )
        .join("\n      ")}
    </div>
  </article>`;
}

function renderCheckinListBody(tickets: Ticket[], creditHits: DeferredCredit[]): string {
  const ticketHtml = tickets.length ? tickets.map(ticketCard).join("") : "";
  const creditHtml = creditHits.length
    ? `<div class="credit-hits" aria-label="7/11 延用搜尋結果">${creditHits.map(deferredCreditHitCard).join("")}</div>`
    : "";
  if (!ticketHtml && !creditHtml) {
    return `<div class="empty"><span>∅</span><p>找不到符合的購票或延用額度</p><small>${escapeHtml(searchEmptyHint(state.searchMode))}</small></div>`;
  }
  return `${ticketHtml}${creditHtml}`;
}

async function redeemDeferredCredit(creditId: string, pkg: "afternoon" | "evening" | "both") {
  if (!canSellField(state.deviceRole)) {
    showToast("此裝置角色不能賣現場票");
    return;
  }
  const credit = state.deferredCredits.find((item) => item.id === creditId);
  if (!credit || credit.redeemed) {
    showToast("找不到可用延用額度");
    return;
  }
  const listPrice = ONSITE_DOOR_PRICES[pkg];
  const prior = Number(credit.priorCredit) || 0;
  const delta = listPrice - prior;
  let ticket = buildDeferredRedeemTicket(credit, pkg);
  ticket.priorCredit = prior;
  ticket.listPrice = listPrice;
  ticket.settleDelta = delta;
  ticket.settleStatus = delta > 0 ? "await_topup" : delta < 0 ? "await_refund" : "clear";
  ticket.couponEligible = couponEligibleOnRedeem(credit);
  ticket.couponIssued = credit.couponIssued;
  ticket.creditSourceId = credit.id;
  ticket.paymentMethod =
    delta > 0
      ? `延用 7/11 票款，現場補 $${delta}`
      : delta < 0
        ? `延用 7/11 票款，現場退 $${Math.abs(delta)}`
        : "延用 7/11 票款，金額相同";
  ticket.paymentRecorded = delta <= 0;

  // 場日模式立刻給穩定 id 並寫 outbox，讓結算／發券／核票 mutation 能對到同一張票
  if (isFieldMode(state.config.endpoint) && !state.demo) {
    const stableId = `${storage.getDeviceId()}-${createId().slice(0, 8)}`;
    ticket = remapTicketId(ticket, stableId);
    state.tickets = [ticket, ...state.tickets];
    state.deferredCredits = state.deferredCredits.map((item) =>
      item.id === credit.id ? { ...item, redeemed: true, redeemedTicketId: ticket.id } : item,
    );
    await enqueueFieldMutation({
      kind: "field_ticket_create",
      mutationId: createId(),
      eventId: EVENT_ID,
      ticketId: ticket.id,
      deviceId: storage.getDeviceId(),
      ticket,
      createdAt: new Date().toISOString(),
    });
    await enqueueFieldMutation({
      kind: "redeem_deferred",
      mutationId: createId(),
      eventId: EVENT_ID,
      ticketId: ticket.id,
      creditSourceId: credit.id,
      couponIssued: ticket.couponIssued,
      deviceId: storage.getDeviceId(),
      createdAt: new Date().toISOString(),
    });
  } else {
    state.tickets = [ticket, ...state.tickets];
    state.deferredCredits = state.deferredCredits.map((item) =>
      item.id === credit.id ? { ...item, redeemed: true, redeemedTicketId: ticket.id } : item,
    );
    await persistTickets();
  }

  // 帶到早鳥／預售核票卡：切場次（必要時）、搜尋鎖定、展開該卡
  if (pkg === "evening") state.activeSession = "evening";
  else if (pkg === "afternoon") state.activeSession = "afternoon";
  state.view = "checkin";
  state.filter = "all";
  state.query = (credit.phone || credit.name || "").trim();
  state.searchMode = credit.phone ? "phone" : "name";
  state.selectedId = ticket.id;
  showToast(
    delta > 0
      ? `已帶入核票｜${credit.name || "延用"}｜請收 $${delta}`
      : delta < 0
        ? `已帶入核票｜${credit.name || "延用"}｜請退 $${Math.abs(delta)}`
        : `已帶入核票｜${credit.name || "延用"}｜無需補退`,
  );
  render();
}

async function settleTicketAction(
  ticketId: string,
  kind: "settle_topup" | "settle_refund" | "coupon_issue",
) {
  const ticket = state.tickets.find((item) => item.id === ticketId);
  if (!ticket) return;
  if (state.demo || !state.config.endpoint) {
    showToast("展示模式無法寫入結算");
    return;
  }
  const mutation: FieldMutation = {
    kind,
    mutationId: createId(),
    eventId: EVENT_ID,
    ticketId,
    deviceId: storage.getDeviceId(),
    createdAt: new Date().toISOString(),
    creditSourceId: ticket.creditSourceId,
    amount: Math.abs(Number(ticket.settleDelta) || 0),
  };
  if (isFieldMode(state.config.endpoint)) {
    await enqueueFieldMutation(mutation);
  } else {
    state.tickets = applyFieldMutationLocally(state.tickets, mutation).map(normalizeTicket);
    await persistTickets();
  }
  showToast(
    kind === "settle_topup"
      ? "已記錄現場補款"
      : kind === "settle_refund"
        ? "已記錄現場退款"
        : "已標記折價券發放",
  );
  render();
}

/** 現場表指名：寫入本機＋ outbox assignment；未指名的晚場可留空，不阻同步。 */
async function commitFieldPerformerFromDom(
  ticketId: string,
  unitId: string,
): Promise<{ ticketId: string; unitId: string } | null> {
  const select = app.querySelector(
    `select[data-field-performer][data-id="${CSS.escape(ticketId)}"][data-unit="${CSS.escape(unitId)}"]`,
  ) as HTMLSelectElement | null;
  if (!select) return null;
  const performer = select.value;
  let ticket = state.tickets.find((item) => item.id === ticketId);
  let unit = ticket?.units?.find((item) => item.id === unitId);
  if (!ticket || !unit) return null;
  if (performer === unit.performer) return { ticketId, unitId };

  // Orphan local-*：先穩定化再寫 assignment（禁止 Dexie-only 指名）。
  if (isLocalDraftTicketId(ticket.id) && isFieldMode(state.config.endpoint) && !state.demo) {
    const seat = unit.seat;
    const session = unit.session;
    ticket = await materializeLocalFieldDraft(ticket);
    ticketId = ticket.id;
    unit = (ticket.units || []).find((item) => item.seat === seat && item.session === session);
    if (!unit) return null;
    unitId = unit.id;
  }

  const mutation: PerformerMutation = {
    kind: "assignment",
    mutationId: createId(),
    eventId: EVENT_ID,
    ticketId,
    unitId,
    session: unit.session,
    performer,
    expectedPerformer: unit.performer,
    deviceId: storage.getDeviceId(),
    createdAt: new Date().toISOString(),
  };
  applyMutationLocally(mutation);

  if (isLocalDraftTicketId(ticketId)) {
    // 非場日草稿：指名只留本機，建立伺服器列時再帶上。
    void persistTickets();
    return { ticketId, unitId };
  }
  if (isFieldMode(state.config.endpoint) && !state.demo) {
    void fieldMutationRequiresOutbox(mutation.kind);
    void appendOutbox(mutation).then(() => {
      state.pendingOutbox += 1;
      render();
    });
    void persistTickets();
    return { ticketId, unitId };
  }
  if (!state.demo && state.config.endpoint) {
    state.queue.push(mutation);
    storage.setQueue(state.queue);
  }
  return { ticketId, unitId };
}

async function changeTicketOps(ticketId: string, opsStatus: TicketOpsStatus) {
  const ticket = state.tickets.find((item) => item.id === ticketId);
  if (!ticket) return;
  if ((ticket.opsStatus || "") === opsStatus) return;
  if (ticket.source === "field" && opsStatus === "refund") {
    showToast("現場／公關請用取消核票＝退款，不做退票註記");
    render();
    return;
  }
  if (state.demo || !state.config.endpoint) {
    showToast("展示模式無法改票務狀態");
    render();
    return;
  }
  if (isFieldMode(state.config.endpoint)) {
    await enqueueFieldMutation({
      kind: "ticket_ops",
      mutationId: createId(),
      eventId: EVENT_ID,
      ticketId,
      opsStatus,
      deviceId: storage.getDeviceId(),
      createdAt: new Date().toISOString(),
    });
    state.pendingOutbox = Math.max(state.pendingOutbox, 1);
    render();
    showToast(opsStatus === "refund" ? "已標記退票（本機）" : "已更新票務狀態（本機）");
    return;
  }
  try {
    const response = await updateTicketOps(state.config, {
      ticketId,
      opsStatus,
      deviceId: storage.getDeviceId(),
    });
    if (response.ticket) {
      const next = normalizeTicket(response.ticket);
      state.tickets = state.tickets.map((item) => (item.id === ticketId ? next : item));
      storage.setTickets(state.tickets);
      render();
      showToast(opsStatus === "refund" ? "已標記退票" : "已更新票務狀態");
    }
  } catch (error) {
    showToast(error instanceof Error ? error.message : "更新票務狀態失敗");
    render();
  }
}

async function mutate(ticketId: string, unitId: string) {
  let ticket = state.tickets.find((item) => item.id === ticketId);
  let unit = ticket?.units?.find((item) => item.id === unitId);
  if (!ticket || !unit) {
    showToast("找不到這張票，請重新同步名單");
    return;
  }
  if (!allowsCheckIn(ticket)) {
    showToast("已退票，無法核票");
    return;
  }
  const delta: 1 | -1 = unit.checkedIn ? -1 : 1;
  const seat = unit.seat;
  const session = unit.session;

  // 現場／公關：確保 DOM 指名已寫入本機／outbox（可能順便 materialize local-*）
  if (delta === 1 && ticket.source === "field") {
    const committed = await commitFieldPerformerFromDom(ticketId, unitId);
    if (committed) {
      ticketId = committed.ticketId;
      unitId = committed.unitId;
    }
    ticket = state.tickets.find((item) => item.id === ticketId);
    unit = ticket?.units?.find((item) => item.id === unitId);
    if (!ticket || !unit) {
      showToast("找不到這張票，請重新同步名單");
      return;
    }
  }

  if (
    delta === 1 &&
    paymentNeedsAttention(ticket) &&
    !(
      state.forceCheckinPrompt?.ticketId === ticketId &&
      state.forceCheckinPrompt?.unitId === unitId
    )
  ) {
    state.forceCheckinPrompt = { ticketId, unitId };
    render();
    return;
  }
  state.forceCheckinPrompt = null;

  // local-* 必須先變成穩定列（場日立刻 outbox；否則先打 API）後才能核票
  if (isLocalDraftTicketId(ticket.id) && !state.demo && state.config.endpoint) {
    try {
      const flushed = await flushFieldTicketBeforeCheckin(ticket, unit);
      ticket = flushed.ticket;
      ticketId = ticket.id;
      unitId = flushed.unitId;
      unit = ticket.units?.find((item) => item.id === unitId);
      if (!unit) {
        showToast("找不到座席，請再試一次");
        return;
      }
    } catch (error) {
      showToast(error instanceof Error ? error.message : "現場票建立失敗");
      render();
      return;
    }
  }

  // Re-resolve after any remap (seat/session stable)
  ticket = state.tickets.find((item) => item.id === ticketId) || ticket;
  unit =
    ticket.units?.find((item) => item.id === unitId) ||
    ticket.units?.find((item) => item.seat === seat && item.session === session) ||
    unit;
  if (!unit) {
    showToast("找不到座席，請再試一次");
    return;
  }
  ticketId = ticket.id;
  unitId = unit.id;

  const mutation: CheckInMutation = {
    kind: "checkin",
    mutationId: createId(),
    eventId: EVENT_ID,
    ticketId,
    unitId,
    session: unit.session,
    delta,
    expectedCount: unit.checkedIn ? 1 : 0,
    deviceId: storage.getDeviceId(),
    createdAt: new Date().toISOString(),
  };
  applyMutationLocally(mutation);
  if (isFieldMode(state.config.endpoint) && !state.demo) {
    void enqueueFieldMutation(mutation).then(() => {
      state.pendingOutbox = Math.max(state.pendingOutbox, 1);
      render();
    });
    render();
    showToast(
      delta === 1
        ? paymentNeedsAttention(ticket)
          ? "已強制核票＝收款（本機）"
          : "已核票＝收款（本機）"
        : "已取消核票＝退款（本機）",
    );
    if (navigator.vibrate && delta === 1) navigator.vibrate(60);
    return;
  }
  if (!state.demo && state.config.endpoint) {
    state.queue.push(mutation);
    storage.setQueue(state.queue);
  }
  render();
  showToast(
    delta === 1
      ? paymentNeedsAttention(ticket)
        ? "已強制核票＝收款"
        : "已核票＝收款"
      : "已取消核票＝退款",
  );
  if (navigator.vibrate && delta === 1) navigator.vibrate(60);
  void syncQueue();
}

async function assignPerformer(ticketId: string, unitId: string, performer: string) {
  let ticket = state.tickets.find((item) => item.id === ticketId);
  let unit = ticket?.units?.find((item) => item.id === unitId);
  if (!ticket || !unit || performer === unit.performer) return;

  if (isLocalDraftTicketId(ticket.id) && isFieldMode(state.config.endpoint) && !state.demo) {
    const seat = unit.seat;
    const session = unit.session;
    ticket = await materializeLocalFieldDraft(ticket);
    ticketId = ticket.id;
    unit = (ticket.units || []).find((item) => item.seat === seat && item.session === session);
    if (!unit) return;
    unitId = unit.id;
  }

  const mutation: PerformerMutation = {
    kind: "assignment",
    mutationId: createId(),
    eventId: EVENT_ID,
    ticketId,
    unitId,
    session: unit.session,
    performer,
    expectedPerformer: unit.performer,
    deviceId: storage.getDeviceId(),
    createdAt: new Date().toISOString(),
  };
  applyMutationLocally(mutation);
  // 非場日本機草稿：指名只留本機，建立伺服器列時再帶上
  if (isLocalDraftTicketId(ticketId)) {
    void persistTickets();
    render();
    return;
  }
  if (isFieldMode(state.config.endpoint) && !state.demo) {
    void enqueueFieldMutation(mutation);
    render();
    return;
  }
  if (!state.demo && state.config.endpoint) {
    state.queue.push(mutation);
    storage.setQueue(state.queue);
  }
  render();
  void syncQueue();
}

function adoptServerTicket(serverTicket: Ticket) {
  const index = state.tickets.findIndex((ticket) => ticket.id === serverTicket.id);
  if (index < 0) return;
  state.tickets[index] = withPendingMutations(normalizeTicket(serverTicket), state.queue);
}

let syncQueueTail: Promise<boolean> = Promise.resolve(true);

async function syncQueueOnce(): Promise<boolean> {
  if (!state.online || !state.config.endpoint || state.demo || !state.queue.length) return true;
  const pending = [...state.queue];
  for (const mutation of pending) {
    if (!state.queue.some((item) => item.mutationId === mutation.mutationId)) continue;
    try {
      const result = await sendMutation(state.config, mutation);
      state.queue = state.queue.filter((item) => item.mutationId !== mutation.mutationId);
      if (result.ticket) adoptServerTicket(result.ticket);
      storage.setQueue(state.queue);
      storage.setTickets(state.tickets);
    } catch (error) {
      if (error instanceof MutationConflictError) {
        const response = error.response;
        state.queue = state.queue.filter((item) => item.mutationId !== mutation.mutationId);
        if (response.ticket) adoptServerTicket(response.ticket);
        state.conflicts.push({
          id: mutation.mutationId,
          mutation,
          message: error.message,
          actualCount: response.conflict?.actualCount,
          ticket: response.ticket,
          detectedAt: new Date().toISOString(),
        });
        storage.setQueue(state.queue);
        storage.setConflicts(state.conflicts);
        storage.setTickets(state.tickets);
        showToast("發現同步衝突，請人工確認");
        continue;
      }
      showToast(error instanceof Error ? error.message : "同步失敗，已保留離線紀錄");
      return false;
    }
  }
  render();
  return true;
}

function syncQueue(): Promise<boolean> {
  const run = syncQueueTail.then(syncQueueOnce, syncQueueOnce);
  syncQueueTail = run.then(
    () => true,
    () => true,
  );
  return run;
}

async function refresh() {
  if (!state.config.endpoint) {
    document.querySelector<HTMLDialogElement>("#settings-dialog")?.showModal();
    return;
  }
  if (isFieldMode(state.config.endpoint)) {
    if (!state.hasCachedSnapshot) state.initialLoading = true;
    render();
    try {
      await checkpointSync({ quiet: state.hasCachedSnapshot });
    } finally {
      // Always clear spinner + re-paint: checkpointSync may have filled Dexie
      // while initialLoading was still true; skipping render() left a fake spinner.
      state.initialLoading = false;
      render();
    }
    return;
  }
  if (state.syncing) return;
  state.initialLoading = !state.hasCachedSnapshot && !state.demo;
  state.syncing = true;
  render();
  try {
    // Load roster/options even if offline mutations are still draining.
    const listPromise = fetchTickets(state.config);
    const feePromise = fetchFeePayouts(state.config).catch(() => null);
    const cashPromise = fetchCashCounts(state.config).catch(() => null);
    const queuePromise = syncQueue();
    const response = await listPromise;
    state.tickets = response.tickets
      .map(normalizeTicket)
      .map((ticket) => withPendingMutations(ticket, state.queue));
    state.performerOptions = {
      afternoon: response.performerOptions?.afternoon?.length
        ? response.performerOptions.afternoon
        : optionsFromTickets(state.tickets, "afternoon"),
      evening: response.performerOptions?.evening?.length
        ? response.performerOptions.evening
        : optionsFromTickets(state.tickets, "evening"),
    };
    const feeResponse = await feePromise;
    if (feeResponse?.ok) state.feePayouts = withPendingFeeMutations(feeResponse.payouts);
    const cashResponse = await cashPromise;
    if (cashResponse?.ok) adoptServerCashCounts(cashResponse.counts);
    state.demo = false;
    state.hasCachedSnapshot = true;
    state.lastSync = new Date().toISOString();
    storage.setTickets(state.tickets);
    storage.setPerformerOptions(state.performerOptions);
    storage.setLastSync(state.lastSync);
    render();
    await queuePromise;
  } catch (error) {
    showToast(error instanceof Error ? error.message : "無法讀取購票資料");
  } finally {
    state.initialLoading = false;
    state.syncing = false;
    render();
  }
}

async function loadFeePayouts() {
  if (state.demo || !state.config.endpoint) return;
  // Field mode: fees live in Dexie from bootstrap／checkpoint — never remote listFeePayouts.
  if (isFieldMode(state.config.endpoint)) {
    try {
      const local = await loadLocalSnapshot();
      state.feePayouts = local.feePayouts;
      render();
    } catch (error) {
      showToast(error instanceof Error ? error.message : "讀取指名費失敗");
    }
    return;
  }
  if (!state.online) return;
  try {
    const response = await fetchFeePayouts(state.config);
    state.feePayouts = withPendingFeeMutations(response.payouts);
    render();
  } catch (error) {
    showToast(error instanceof Error ? error.message : "讀取指名費失敗");
  }
}

function upsertLocalFeePayout(session: Session, performer: string, amountPaid: number) {
  state.feePayouts = [
    ...state.feePayouts.filter((item) => !(item.session === session && item.performer === performer)),
    {
      session,
      performer,
      amountPaid,
      updatedAt: new Date().toISOString(),
      device: storage.getDeviceId(),
    },
  ];
}

function withPendingFeeMutations(payouts: FeePayoutRecord[]): FeePayoutRecord[] {
  const map = new Map(payouts.map((item) => [`${item.session}:${item.performer}`, item] as const));
  for (const pending of state.feeQueue) {
    map.set(`${pending.session}:${pending.performer}`, {
      session: pending.session,
      performer: pending.performer,
      amountPaid: pending.amountPaid,
    });
  }
  return [...map.values()];
}

function enqueueFeePayout(session: Session, performer: string, amountPaid: number) {
  state.feeQueue = [
    ...state.feeQueue.filter((item) => !(item.session === session && item.performer === performer)),
    { session, performer, amountPaid },
  ];
}

let feeSyncTail: Promise<void> = Promise.resolve();

async function syncFeeQueueOnce() {
  if (!state.online || !state.config.endpoint || state.demo || !state.feeQueue.length) return;
  const pending = [...state.feeQueue];
  for (const mutation of pending) {
    const stillQueued = state.feeQueue.find(
      (item) =>
        item.session === mutation.session &&
        item.performer === mutation.performer &&
        item.amountPaid === mutation.amountPaid,
    );
    if (!stillQueued) continue;
    try {
      const response = await saveFeePayout(state.config, {
        session: mutation.session,
        performer: mutation.performer,
        amountPaid: mutation.amountPaid,
        deviceId: storage.getDeviceId(),
      });
      state.feeQueue = state.feeQueue.filter(
        (item) =>
          !(
            item.session === mutation.session &&
            item.performer === mutation.performer &&
            item.amountPaid === mutation.amountPaid
          ),
      );
      state.feePayouts = withPendingFeeMutations(response.payouts);
      render();
    } catch (error) {
      showToast(error instanceof Error ? error.message : "指名費同步失敗，已保留本機金額");
      return;
    }
  }
}

function syncFeeQueue() {
  const run = feeSyncTail.then(syncFeeQueueOnce, syncFeeQueueOnce);
  feeSyncTail = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

function persistFeePayout(session: Session, performer: string, amountPaid: number) {
  if (!performer) return;
  if (!Number.isFinite(amountPaid) || amountPaid < 0) {
    showToast("實給金額不正確");
    return;
  }
  if (!canWriteFees(state.deviceRole)) {
    showToast("此裝置角色不能改指名費實給");
    return;
  }
  upsertLocalFeePayout(session, performer, amountPaid);
  if (state.demo || !state.config.endpoint) {
    showToast("已暫存實給（展示模式）");
    render();
    return;
  }
  if (isFieldMode(state.config.endpoint)) {
    void enqueueFieldMutation({
      kind: "fee_payout",
      mutationId: createId(),
      eventId: EVENT_ID,
      session,
      performer,
      amountPaid,
      deviceId: storage.getDeviceId(),
      createdAt: new Date().toISOString(),
    }).then(() => {
      state.pendingOutbox = Math.max(state.pendingOutbox, 1);
      render();
    });
    showToast("已記錄實給（本機；應給由本機票務即時計算）");
    render();
    return;
  }
  enqueueFeePayout(session, performer, amountPaid);
  render();
  showToast(state.online ? "已記錄實給，背景同步中" : "已記錄實給，恢復連線後同步");
  void syncFeeQueue();
}

function adoptServerCashCounts(
  records: Array<{
    checkpoint?: string;
    counts?: Partial<Record<string, number>>;
    balance?: number;
    income?: number;
    feeSnapshot?: number;
    feeLocked?: boolean;
    updatedAt?: string;
    device?: string;
  }>,
) {
  const next = { ...state.cashCounts };
  for (const raw of records) {
    const checkpoint = raw.checkpoint as CashCheckpoint | undefined;
    if (!checkpoint || !(checkpoint in next)) continue;
    if (state.cashQueue.includes(checkpoint)) continue;
    next[checkpoint] = normalizeCashRecord({
      checkpoint,
      counts: normalizeCounts(raw.counts),
      income: raw.income,
      feeSnapshot: raw.feeSnapshot,
      feeLocked: raw.feeLocked,
      updatedAt: raw.updatedAt,
      device: raw.device,
    });
  }
  state.cashCounts = next;
  storage.setCashCounts(state.cashCounts);
}

async function loadCashCounts() {
  if (state.demo || !state.config.endpoint) return;
  // Field mode: cash lives in Dexie from bootstrap／checkpoint — never remote listCashCounts.
  if (isFieldMode(state.config.endpoint)) {
    try {
      const local = await loadLocalSnapshot();
      state.cashCounts = local.cashCounts;
      storage.setCashCounts(state.cashCounts);
      render();
    } catch (error) {
      showToast(error instanceof Error ? error.message : "讀取錢箱失敗");
    }
    return;
  }
  if (!state.online) return;
  try {
    const response = await fetchCashCounts(state.config);
    adoptServerCashCounts(response.counts);
    render();
  } catch (error) {
    showToast(error instanceof Error ? error.message : "讀取錢箱失敗");
  }
}

function enqueueCashCount(checkpoint: CashCheckpoint) {
  state.cashQueue = [...state.cashQueue.filter((item) => item !== checkpoint), checkpoint];
}

let cashSyncTail: Promise<void> = Promise.resolve();

async function syncCashQueueOnce() {
  if (!state.online || !state.config.endpoint || state.demo || !state.cashQueue.length) return;
  const pending = [...state.cashQueue];
  for (const checkpoint of pending) {
    if (!state.cashQueue.includes(checkpoint)) continue;
    const record = state.cashCounts[checkpoint];
    try {
      const response = await saveCashCount(state.config, {
        checkpoint,
        counts: record.counts,
        balance: record.balance,
        income: record.income,
        feeSnapshot: record.feeSnapshot,
        feeLocked: record.feeLocked,
        deviceId: storage.getDeviceId(),
      });
      state.cashQueue = state.cashQueue.filter((item) => item !== checkpoint);
      adoptServerCashCounts(response.counts);
      render();
    } catch (error) {
      showToast(error instanceof Error ? error.message : "錢箱同步失敗，已保留本機金額");
      return;
    }
  }
}

function syncCashQueue() {
  const run = cashSyncTail.then(syncCashQueueOnce, syncCashQueueOnce);
  cashSyncTail = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

function updateCashQty(checkpoint: CashCheckpoint, denomination: Denomination, raw: string) {
  const qty = Math.max(0, Math.floor(Number(raw) || 0));
  const counts = { ...state.cashCounts[checkpoint].counts, [denomination]: qty };
  const record = {
    ...state.cashCounts[checkpoint],
    counts,
    balance: computeBalance(counts),
  };
  state.cashCounts = { ...state.cashCounts, [checkpoint]: record };
}

function paintCashDerived(checkpoint: CashCheckpoint) {
  const section = app.querySelector<HTMLElement>(`.cash-block[data-checkpoint="${checkpoint}"]`);
  if (!section) return;
  const record = state.cashCounts[checkpoint];
  const balanceEl = section.querySelector(".cash-balance strong");
  if (balanceEl) balanceEl.textContent = String(record.balance);
  for (const denomination of DENOMINATIONS) {
    const row = section.querySelector(`input[data-denomination="${denomination}"]`)?.closest(".cash-denom-row");
    const subtotal = row?.querySelector(".cash-subtotal");
    if (subtotal) subtotal.textContent = String(denomination * (record.counts[denomination] || 0));
  }
  const prev = previousCheckpoint(checkpoint);
  if (!prev) return;
  const doorIncome = computeDoorIncome(state.tickets, checkpoint === "afternoon" ? "afternoon" : "evening");
  const balanceDelta = computeBalanceDelta(record.balance, state.cashCounts[prev].balance);
  const variance = computeVariance({
    balanceDelta,
    income: doorIncome,
    feeSnapshot: record.feeSnapshot,
  });
  const deltaStrong = section.querySelector(".cash-recon > div:first-child strong");
  if (deltaStrong) deltaStrong.textContent = String(balanceDelta);
  const incomeEl = section.querySelector(".cash-income-value");
  if (incomeEl) incomeEl.textContent = String(doorIncome);
  const varianceBox = section.querySelector(".cash-variance");
  if (varianceBox) {
    varianceBox.classList.toggle("is-flat", variance === 0);
    varianceBox.classList.toggle("is-off", variance !== 0);
    const strong = varianceBox.querySelector("strong");
    const small = varianceBox.querySelector("small");
    if (strong) strong.textContent = String(variance);
    if (small) small.textContent = variance === 0 ? "帳平" : "帳不平";
  }
}

function paintCashAfterEdit(checkpoint: CashCheckpoint) {
  paintCashDerived(checkpoint);
  if (checkpoint === "opening") paintCashDerived("afternoon");
  if (checkpoint === "afternoon") paintCashDerived("evening");
}

function readCashSectionFromDom(checkpoint: CashCheckpoint) {
  const section = app.querySelector<HTMLElement>(`.cash-block[data-checkpoint="${checkpoint}"]`);
  if (!section) return;
  const counts = { ...state.cashCounts[checkpoint].counts };
  for (const denomination of DENOMINATIONS) {
    const input = section.querySelector<HTMLInputElement>(
      `input[data-cash-qty="${checkpoint}"][data-denomination="${denomination}"]`,
    );
    counts[denomination] = Math.max(0, Math.floor(Number(input?.value || 0)));
  }
  state.cashCounts = {
    ...state.cashCounts,
    [checkpoint]: {
      ...state.cashCounts[checkpoint],
      counts,
      balance: computeBalance(counts),
    },
  };
}

function persistCashCount(checkpoint: CashCheckpoint) {
  if (!canWriteCash(state.deviceRole)) {
    showToast("此裝置角色不能改錢箱");
    return;
  }
  readCashSectionFromDom(checkpoint);
  const record = { ...state.cashCounts[checkpoint] };
  record.balance = computeBalance(record.counts);
  if (checkpoint !== "opening") {
    const session = checkpoint === "afternoon" ? "afternoon" : "evening";
    // Snapshot computed door income at save so historical checkpoints don't drift.
    record.income = computeDoorIncome(state.tickets, session);
    if (!record.feeLocked) {
      record.feeSnapshot = buildFeeSnapshot(state.feePayouts, session);
      record.feeLocked = true;
    }
  }
  record.updatedAt = new Date().toISOString();
  record.device = storage.getDeviceId();
  state.cashCounts = { ...state.cashCounts, [checkpoint]: record };
  storage.setCashCounts(state.cashCounts);
  if (state.demo || !state.config.endpoint) {
    showToast("已暫存錢箱（展示模式）");
    render();
    return;
  }
  if (isFieldMode(state.config.endpoint)) {
    void enqueueFieldMutation({
      kind: "cash_count",
      mutationId: createId(),
      eventId: EVENT_ID,
      checkpoint,
      counts: { ...record.counts },
      balance: record.balance,
      income: record.income,
      feeSnapshot: record.feeSnapshot,
      feeLocked: record.feeLocked,
      deviceId: storage.getDeviceId(),
      createdAt: record.updatedAt,
    }).then(() => {
      state.pendingOutbox = Math.max(state.pendingOutbox, 1);
      render();
    });
    showToast("已記錄錢箱（本機）");
    render();
    return;
  }
  enqueueCashCount(checkpoint);
  render();
  showToast(state.online ? "已記錄錢箱，背景同步中" : "已記錄錢箱，恢復連線後同步");
  void syncCashQueue();
}

function refreshCashFeeSnapshot(checkpoint: CashCheckpoint) {
  if (checkpoint === "opening") return;
  readCashSectionFromDom(checkpoint);
  const session = checkpoint === "afternoon" ? "afternoon" : "evening";
  const record = {
    ...state.cashCounts[checkpoint],
    feeSnapshot: buildFeeSnapshot(state.feePayouts, session),
    feeLocked: true,
    updatedAt: new Date().toISOString(),
    device: storage.getDeviceId(),
  };
  state.cashCounts = { ...state.cashCounts, [checkpoint]: record };
  storage.setCashCounts(state.cashCounts);
  if (isFieldMode(state.config.endpoint) && !state.demo && state.config.endpoint) {
    void enqueueFieldMutation({
      kind: "cash_count",
      mutationId: createId(),
      eventId: EVENT_ID,
      checkpoint,
      counts: { ...record.counts },
      balance: record.balance,
      income: record.income,
      feeSnapshot: record.feeSnapshot,
      feeLocked: record.feeLocked,
      deviceId: storage.getDeviceId(),
      createdAt: record.updatedAt,
    }).then(() => {
      state.pendingOutbox = Math.max(state.pendingOutbox, 1);
      render();
    });
    render();
    showToast("已重抓指名費快照（本機）");
    return;
  }
  enqueueCashCount(checkpoint);
  render();
  showToast("已重抓指名費快照");
  void syncCashQueue();
}

async function handleAction(target: HTMLElement, event: Event) {
  const action = target.dataset.action;
  if (action === "select") {
    state.selectedId = state.selectedId === target.dataset.id ? "" : target.dataset.id || "";
    state.forceCheckinPrompt = null;
    render();
  } else if (action === "toggle-checkin") {
    event.preventDefault();
    await mutate(target.dataset.id || "", target.dataset.unit || "");
  } else if (action === "force-checkin-yes") {
    event.preventDefault();
    state.forceCheckinPrompt = {
      ticketId: target.dataset.id || "",
      unitId: target.dataset.unit || "",
    };
    await mutate(target.dataset.id || "", target.dataset.unit || "");
  } else if (action === "force-checkin-no") {
    event.preventDefault();
    state.forceCheckinPrompt = null;
    render();
  } else if (action === "session") {
    state.activeSession = target.dataset.session as Session;
    state.selectedId = "";
    state.forceCheckinPrompt = null;
    render();
  } else if (action === "clear") {
    event.preventDefault();
    state.query = "";
    state.forceCheckinPrompt = null;
    for (const input of app.querySelectorAll<HTMLInputElement>(".search-input")) {
      input.value = "";
    }
    paintSearchClearButton();
    paintPresaleSearchResults();
    activeSearchInput()?.focus();
  } else if (action === "search-mode") {
    event.preventDefault();
    event.stopPropagation();
    const mode: SearchMode = target.dataset.mode === "name" ? "name" : "phone";
    state.searchMode = mode;
    paintSearchModeChrome({ focus: true });
    paintPresaleSearchResults();
  } else if (action === "add-field") {
    event.preventDefault();
    const kind = target.dataset.kind === "pr" ? "pr" : "onsite";
    const pkg = target.dataset.package as "afternoon" | "evening" | "both";
    await addFieldTicket(kind, pkg);
  } else if (action === "settle-topup") {
    event.preventDefault();
    await settleTicketAction(target.dataset.id || "", "settle_topup");
  } else if (action === "settle-refund") {
    event.preventDefault();
    await settleTicketAction(target.dataset.id || "", "settle_refund");
  } else if (action === "issue-coupon") {
    event.preventDefault();
    await settleTicketAction(target.dataset.id || "", "coupon_issue");
  } else if (action === "credit-redeem") {
    event.preventDefault();
    const creditId = target.dataset.credit || "";
    const pkg = (target.dataset.package || "afternoon") as "afternoon" | "evening" | "both";
    await redeemDeferredCredit(creditId, pkg);
  } else if (action === "view-fees") {
    state.view = "fees";
    state.forceCheckinPrompt = null;
    render();
    void loadFeePayouts();
  } else if (action === "view-cash") {
    state.view = "cash";
    state.forceCheckinPrompt = null;
    render();
    void loadFeePayouts();
    void loadCashCounts();
  } else if (action === "view-field") {
    state.view = "field";
    state.forceCheckinPrompt = null;
    render();
    const missingOptions =
      !(state.performerOptions.afternoon?.length && state.performerOptions.evening?.length);
    if (missingOptions && isFieldMode(state.config.endpoint) && state.online) {
      void checkpointSync({ quiet: true });
    }
  } else if (action === "view-checkin") {
    state.view = "checkin";
    state.forceCheckinPrompt = null;
    render();
  } else if (action === "checkpoint-sync") {
    event.preventDefault();
    await checkpointSync();
  } else if (action === "clear-notes") {
    state.autoNotes = [];
    render();
  } else if (action === "save-cash") {
    event.preventDefault();
    persistCashCount(target.dataset.checkpoint as CashCheckpoint);
  } else if (action === "refresh-cash-fee") {
    event.preventDefault();
    refreshCashFeeSnapshot(target.dataset.checkpoint as CashCheckpoint);
  } else if (action === "save-fee") {
    event.preventDefault();
    const session = target.dataset.session as Session;
    const performer = target.dataset.performer || "";
    const input = target.closest("tr")?.querySelector<HTMLInputElement>("input.fee-input");
    const amountPaid = Number(input?.value || 0);
    persistFeePayout(session, performer, amountPaid);
  } else if (action === "settings") {
    document.querySelector<HTMLDialogElement>("#settings-dialog")?.showModal();
  } else if (action === "sync") {
    await refresh();
  } else if (action === "open-conflicts") {
    document.querySelector<HTMLDialogElement>("#conflicts-dialog")?.showModal();
  } else if (action === "close-conflicts") {
    document.querySelector<HTMLDialogElement>("#conflicts-dialog")?.close();
  } else if (action === "dismiss-conflict") {
    const conflictId = target.dataset.conflict || "";
    state.conflicts = state.conflicts.filter((conflict) => conflict.id !== conflictId);
    storage.setConflicts(state.conflicts);
    if (!state.conflicts.length) {
      document.querySelector<HTMLDialogElement>("#conflicts-dialog")?.close();
    }
    render();
  } else if (action === "retry-conflict") {
    const conflictId = target.dataset.conflict || "";
    const conflict = state.conflicts.find((item) => item.id === conflictId);
    if (!conflict) return;
    state.conflicts = state.conflicts.filter((item) => item.id !== conflictId);
    storage.setConflicts(state.conflicts);
    document.querySelector<HTMLDialogElement>("#conflicts-dialog")?.close();
    if (conflict.mutation.kind === "assignment") {
      await assignPerformer(conflict.mutation.ticketId, conflict.mutation.unitId, conflict.mutation.performer);
    } else {
      await mutate(conflict.mutation.ticketId, conflict.mutation.unitId);
    }
  } else if (action === "demo") {
    event.preventDefault();
    state.tickets = structuredClone(DEMO_TICKETS);
    state.demo = true;
    state.hasCachedSnapshot = false;
    state.lastSync = "";
    state.config = { endpoint: "", token: "" };
    storage.setConfig(state.config);
    storage.setTickets(state.tickets);
    storage.clearLastSync();
    document.querySelector<HTMLDialogElement>("#settings-dialog")?.close();
    render();
  } else if (action === "save-settings") {
    event.preventDefault();
    const form = document.querySelector<HTMLFormElement>("#settings-form");
    if (!form?.reportValidity()) return;
    const data = new FormData(form);
    const wasDemo = state.demo;
    state.config = {
      endpoint: String(data.get("endpoint") || "").trim(),
      token: String(data.get("token") || "").trim(),
    };
    const role = String(data.get("deviceRole") || "general") as DeviceRole;
    state.deviceRole = role;
    storage.setDeviceRole(role);
    storage.setFieldToken(state.config.token);
    state.demo = false;
    if (wasDemo) {
      state.tickets = [];
      state.hasCachedSnapshot = false;
      state.lastSync = "";
      storage.setTickets([]);
      storage.clearLastSync();
    }
    storage.setConfig(state.config);
    document.querySelector<HTMLDialogElement>("#settings-dialog")?.close();
    if (isFieldMode(state.config.endpoint) && state.online) {
      void fieldSetDeviceRole(fieldConfig(), storage.getDeviceId(), role).catch(() => undefined);
    }
    await refresh();
  }
}

function activeSearchInput(): HTMLInputElement | null {
  return app.querySelector<HTMLInputElement>(
    state.searchMode === "name" ? "#search-name" : "#search-phone",
  );
}

function paintSearchClearButton() {
  const clear = app.querySelector<HTMLButtonElement>(".search-clear");
  if (!clear) return;
  clear.hidden = !state.query;
}

function paintSearchModeChrome(options: { focus?: boolean } = {}) {
  const phone = app.querySelector<HTMLInputElement>("#search-phone");
  const name = app.querySelector<HTMLInputElement>("#search-name");
  const modeGroup = app.querySelector(".search-mode");
  if (!phone || !name || !modeGroup) return;

  // 以目前可見欄位為準同步 query
  const visible = phone.hidden ? name : phone;
  state.query = visible.value;
  phone.value = state.query;
  name.value = state.query;

  phone.hidden = state.searchMode !== "phone";
  name.hidden = state.searchMode !== "name";

  for (const button of modeGroup.querySelectorAll<HTMLButtonElement>("[data-mode]")) {
    const active = button.dataset.mode === state.searchMode;
    button.classList.toggle("active", active);
    button.setAttribute("aria-pressed", active ? "true" : "false");
  }
  paintSearchClearButton();

  if (!options.focus) return;
  // 必須在 click 手勢內同步 focus，iOS 才會叫出對應鍵盤
  const next = activeSearchInput();
  next?.focus();
}

function paintPresaleSearchResults() {
  if (state.view !== "checkin") {
    paintSearchClearButton();
    return;
  }
  const tickets = filteredTickets();
  const creditHits = filteredDeferredCredits();
  const list = app.querySelector(".ticket-list");
  if (list) {
    list.innerHTML = renderCheckinListBody(tickets, creditHits);
  }
  const headTitle = app.querySelector(".result-head h2");
  const headCount = app.querySelector(".result-head span");
  if (headTitle) headTitle.textContent = state.query ? "搜尋結果" : "預售／早鳥名單";
  if (headCount) {
    headCount.textContent = `${tickets.length} 筆${creditHits.length ? ` · ${creditHits.length} 延用` : ""}`;
  }
  paintSearchClearButton();
}

function bindGlobalEvents() {
  app.addEventListener("click", (event) => {
    const filter = (event.target as HTMLElement | null)?.closest?.("[data-filter]") as HTMLElement | null;
    if (filter && app.contains(filter)) {
      state.filter = filter.dataset.filter as Filter;
      state.forceCheckinPrompt = null;
      render();
      return;
    }
    const target = (event.target as HTMLElement | null)?.closest?.("[data-action]") as HTMLElement | null;
    if (!target || !app.contains(target)) return;
    // 點在輸入框／表單內文字欄位時不要當成 action（避免整頁重繪搶焦點）
    if (target.tagName === "SELECT" || target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "FORM") {
      return;
    }
    const clicked = event.target as HTMLElement | null;
    if (clicked && (clicked.tagName === "INPUT" || clicked.tagName === "TEXTAREA")) return;
    void handleAction(target, event);
  });

  app.addEventListener("change", (event) => {
    const target = event.target as HTMLSelectElement | null;
    if (!target) return;
    if (target.matches("select[data-field-performer]")) {
      void commitFieldPerformerFromDom(target.dataset.id || "", target.dataset.unit || "");
      return;
    }
    if (target.dataset.action === "assign-performer") {
      void assignPerformer(target.dataset.id || "", target.dataset.unit || "", target.value);
      return;
    }
    if (target.dataset.action === "update-ops") {
      void changeTicketOps(target.dataset.id || "", (target.value || "") as TicketOpsStatus);
    }
  });

  // 搜尋只更新名單，不整頁 render，避免手機數字鍵盤被打掉
  app.addEventListener("input", (event) => {
    const target = event.target as HTMLInputElement | null;
    if (!target) return;
    if (target.classList.contains("search-input")) {
      state.query = target.value;
      const other = app.querySelector<HTMLInputElement>(
        target.id === "search-phone" ? "#search-name" : "#search-phone",
      );
      if (other) other.value = state.query;
      paintPresaleSearchResults();
      // 末三碼打滿就收鍵盤，方便直接點結果
      if (target.id === "search-phone" && target.value.replace(/\D/g, "").length === 3) {
        target.blur();
      }
      return;
    }
    const qtyCheckpoint = target.dataset.cashQty as CashCheckpoint | undefined;
    if (qtyCheckpoint) {
      const denomination = Number(target.dataset.denomination) as Denomination;
      updateCashQty(qtyCheckpoint, denomination, target.value);
      paintCashAfterEdit(qtyCheckpoint);
      return;
    }
  });
}

window.addEventListener("online", () => {
  state.online = true;
  render();
  if (isFieldMode(state.config.endpoint)) {
    // Field mode: sync only at checkpoints; do not auto-drain on reconnect.
    return;
  }
  void refresh();
  void syncFeeQueue();
  void syncCashQueue();
});
window.addEventListener("offline", () => {
  state.online = false;
  render();
});
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible" && state.online && !isFieldMode(state.config.endpoint)) {
    void refresh();
  }
});

if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("./sw.js");
  });
}

async function bootFieldLocal() {
  if (!isFieldMode(state.config.endpoint)) return;
  try {
    const local = await loadLocalSnapshot();
    if (local.tickets.length) {
      state.tickets = local.tickets.map(normalizeTicket);
      state.feePayouts = local.feePayouts;
      state.cashCounts = local.cashCounts;
      state.deferredCredits = local.deferredCredits || [];
      state.performerOptions = mergePerformerOptions(
        local.performerOptions,
        storage.getPerformerOptions(),
      );
      storage.setPerformerOptions(state.performerOptions);
      state.pendingOutbox = local.outbox.length;
      state.lastSync = local.meta.lastSync;
      state.hasCachedSnapshot = true;
      state.demo = false;
      if (local.meta.deviceRole) {
        state.deviceRole = local.meta.deviceRole;
        storage.setDeviceRole(local.meta.deviceRole);
      }
    } else if (state.tickets.length) {
      await replaceLocalSnapshot({
        tickets: state.tickets,
        feePayouts: state.feePayouts,
        cashCounts: Object.values(state.cashCounts),
        performerOptions: state.performerOptions,
        ssot: "db",
      });
    }
    await setFieldMeta({ deviceRole: state.deviceRole });
  } catch {
    // IndexedDB unavailable — fall back to localStorage snapshot already in state.
  }
}

bindGlobalEvents();
void bootFieldLocal().then(() => {
  render();
  if (state.config.endpoint) void refresh();
});
